import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { downloadFile } from "../api/client";
import {
  Customer,
  DeviceGroup,
  Device,
  OnboardingPackage,
  TimeEntry,
  TimeTotals,
  CustomerNote,
  Ticket,
  PortalCustomerUser,
  AlertThreshold,
  Alert,
  PatchPolicy,
  SLAPolicy,
  MaintenanceWindow,
  CustomerCompliance,
  Runbook,
  RunbookStep,
  Credential,
  CredentialReveal,
} from "../types";
import {
  PolicyChipPicker,
  KbListEditor,
  SEVERITY_OPTIONS,
  CATEGORY_OPTIONS,
} from "../components/PolicyEditors";
import PasswordResetModal from "../components/PasswordResetModal";
import SLAPolicyEditor from "../components/SLAPolicyEditor";
import CloudConnectorsCard from "../components/CloudConnectorsCard";
import ManualLicensesCard from "../components/ManualLicensesCard";
import MerakiLicensesCard from "../components/MerakiLicensesCard";
import { useAuth } from "../context/AuthContext";
import { formatDate, formatDateTime, formatRelative, utcMillis } from "../utils/datetime";
import ADUsers from "./ADUsers";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import DeployResultPanel from "../components/DeployResultPanel";
import ActionButton from "../components/ActionButton";

// Local rank maps so severity/priority/status columns sort in logical
// order rather than alphabetically.
const TICKET_PRIORITY_RANK: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
  urgent: 3,
};
const TICKET_STATUS_RANK: Record<string, number> = {
  open: 0,
  in_progress: 1,
  on_hold: 2,
  resolved: 3,
  closed: 4,
};
const ALERT_SEVERITY_RANK: Record<string, number> = {
  info: 0,
  warning: 1,
  critical: 2,
};
// IPv4 → sortable integer; non-numeric/empty sort first.
function ipv4ToNumber(ip?: string): number {
  if (!ip) return 0;
  const parts = ip.split(".");
  if (parts.length !== 4) return 0;
  let n = 0;
  for (const p of parts) {
    const o = Number(p);
    if (!Number.isInteger(o) || o < 0 || o > 255) return 0;
    n = n * 256 + o;
  }
  return n;
}

type Tab =
  | "devices"
  | "groups"
  | "onboarding"
  | "tickets"
  | "time"
  | "notes"
  | "billing"
  | "portal"
  | "reports"
  | "ad"
  | "runbooks"
  | "credentials"
  | "cloud"
  | "meshcentral"
  | "meraki_licenses";

function currentMonthString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function fmtMinutes(mins?: number): string {
  if (mins == null) return "—";
  return `${(mins / 60).toFixed(2)} hr`;
}
function fmtCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "technician";
  const isAdmin = user?.role === "admin";
  const canViewBilling = isAdmin || !!user?.can_view_billing;

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [groups, setGroups] = useState<DeviceGroup[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [packages, setPackages] = useState<OnboardingPackage[]>([]);

  // Click-to-sort state — one per table (backlog #40).
  const pkgSort = useSort("created_at", "desc");
  const ticketSort = useSort("updated_at", "desc");
  const timeSort = useSort("started_at", "desc");
  const alertSort = useSort("created_at", "desc");
  const rbSort = useSort("title", "asc");
  const credSort = useSort("label", "asc");
  const portalSort = useSort("email", "asc");
  // Deep-link support: ?tab=meraki_licenses lands on that tab on load.
  // Lets MerakiLicenseSummary's "Licenses" link jump straight there
  // without an extra click.
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as Tab) || "devices";
  const [tab, setTab] = useState<Tab>(initialTab);

  // Time
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [totals, setTotals] = useState<TimeTotals | null>(null);
  const [month, setMonth] = useState(currentMonthString());
  const [newEntry, setNewEntry] = useState({
    description: "",
    duration_hours: 0.5,
    billable: true,
    started_at: new Date().toISOString().slice(0, 16),
  });
  const [runningTimer, setRunningTimer] = useState<TimeEntry | null>(null);
  // Inline row edit on the time-entries table. Null = not editing; otherwise
  // the id of the row currently in edit mode.
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    description: "",
    started_at: "",
    duration_hours: 0.5,
  });

  // Notes
  const [notes, setNotes] = useState<CustomerNote[]>([]);
  const [newNote, setNewNote] = useState("");
  // Inline note edit. Null = not editing; otherwise the note's id.
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editNoteBody, setEditNoteBody] = useState("");

  // Tickets
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [ticketsIncludeClosed, setTicketsIncludeClosed] = useState(false);

  // Reports
  const [reportMonth, setReportMonth] = useState(currentMonthString());
  const [reportPriorMonth, setReportPriorMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  // Billing tab → Monthly Statement period. Defaults to current month
  // so freshly-logged time shows up immediately (the export endpoint
  // defaults to prior month, which is the wrong UX after you just
  // added an entry and click over to Billing).
  const [statementMonth, setStatementMonth] = useState(currentMonthString());
  const [reportDays, setReportDays] = useState(30);
  const [reportIncludeClosed, setReportIncludeClosed] = useState(true);

  // Alert thresholds + active alerts
  const [globalThresholds, setGlobalThresholds] = useState<AlertThreshold[]>([]);
  const [customerThresholds, setCustomerThresholds] = useState<AlertThreshold[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<Alert[]>([]);
  const [overrideDraft, setOverrideDraft] = useState<Record<string, AlertThreshold>>({});
  const [overrideSaved, setOverrideSaved] = useState<string | null>(null);

  // Patch approval policy override for this customer (falls back to global)
  const [globalPatchPolicy, setGlobalPatchPolicy] = useState<PatchPolicy | null>(null);
  const [, setCustomerPatchPolicy] = useState<PatchPolicy | null>(null);
  const [patchPolicyDraft, setPatchPolicyDraft] = useState<PatchPolicy | null>(null);
  const [patchPolicyHasOverride, setPatchPolicyHasOverride] = useState(false);

  // SLA policy override for this customer (falls back to global)
  const [globalSlaPolicy, setGlobalSlaPolicy] = useState<SLAPolicy | null>(null);
  const [slaDraft, setSlaDraft] = useState<SLAPolicy | null>(null);
  const [slaHasOverride, setSlaHasOverride] = useState(false);
  const [slaSaved, setSlaSaved] = useState(false);

  // Reports tab — split into "Insights" (read-only data + email
  // deliverables) and "Policies" (admin-editable controls). Sub-tab
  // state is local, not URL-persisted.
  const [reportsSubTab, setReportsSubTab] = useState<"insights" | "policies">("insights");

  // SLA breach summary email (customer-facing weekly digest)
  const [slaSummaryDays, setSlaSummaryDays] = useState(7);
  const [slaSummarySendOpen, setSlaSummarySendOpen] = useState(false);
  const [slaSummaryRecipient, setSlaSummaryRecipient] = useState("");
  const [slaSummaryForce, setSlaSummaryForce] = useState(false);
  const [slaSummarySending, setSlaSummarySending] = useState(false);
  const [slaSummaryResult, setSlaSummaryResult] = useState<string | null>(null);

  // Runbooks for this customer: shows this customer's scoped runbooks + globals.
  // The list view lets a tech see everything that applies; create defaults
  // scope to "this customer" but the editor can flip it to Global.
  const [runbooks, setRunbooks] = useState<Runbook[]>([]);
  const [rbEditing, setRbEditing] = useState<Runbook | null>(null);
  const [rbCreating, setRbCreating] = useState(false);
  const [rbDraft, setRbDraft] = useState<{
    customer_id: number | null;
    visibility: "internal" | "customer";
    title: string;
    description: string;
    steps: RunbookStep[];
  }>({
    customer_id: null,
    visibility: "internal",
    title: "",
    description: "",
    steps: [{ title: "", body: "" }],
  });
  const [rbSaving, setRbSaving] = useState(false);

  // ── QBR (Quarterly Business Review) ─────────────────────────────────────
  // Quarter labels look like "2026-Q1". The picklist holds the current
  // quarter + the four most recently closed quarters so techs can
  // re-send historical reports too.
  const qbrQuarterOptions = useMemo(() => {
    const now = new Date();
    const out: string[] = [];
    let y = now.getFullYear();
    let q = Math.floor(now.getMonth() / 3) + 1; // 1..4
    for (let i = 0; i < 5; i++) {
      out.push(`${y}-Q${q}`);
      q -= 1;
      if (q === 0) {
        q = 4;
        y -= 1;
      }
    }
    return out;
  }, []);
  // Default to the most recently CLOSED quarter (index 1; index 0 is current).
  const [qbrQuarter, setQbrQuarter] = useState<string>("");
  useEffect(() => {
    if (!qbrQuarter && qbrQuarterOptions.length > 1) setQbrQuarter(qbrQuarterOptions[1]);
  }, [qbrQuarter, qbrQuarterOptions]);
  const [qbrSending, setQbrSending] = useState(false);
  const [qbrSentInfo, setQbrSentInfo] = useState<string>("");
  const [qbrEmailing, setQbrEmailing] = useState(false);
  const [qbrEmailOverride, setQbrEmailOverride] = useState("");

  const previewQbr = async () => {
    try {
      const r = await api.get(`/api/v1/qbr/preview`, {
        params: { customer_id: id, quarter: qbrQuarter },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // Browsers hold onto the blob URL while the new tab is open;
      // revoke after a generous delay so we don't leak forever.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to generate QBR preview");
    }
  };
  const downloadQbr = async () => {
    try {
      await downloadFile(
        `/api/v1/qbr/preview`,
        `QBR_${customer?.name || "customer"}_${qbrQuarter}.pdf`,
        { customer_id: id, quarter: qbrQuarter },
      );
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to download QBR");
    }
  };
  const openAssessment = async () => {
    try {
      // Save the PDF to disk (Content-Disposition filename) rather than opening
      // a blob tab, which some browsers block / can't reliably "Save as".
      await downloadFile(
        `/api/v1/reports/assessment/${id}/download.pdf`,
        `Assessment_${customer?.name || "customer"}.pdf`,
      );
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to generate assessment report");
    }
  };
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const openInventory = async () => {
    setInventoryLoading(true);
    try {
      await downloadFile(
        `/api/v1/reports/inventory/${id}/download.pdf`,
        `Inventory_${customer?.name || "customer"}.pdf`,
      );
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to generate inventory report");
    } finally {
      setInventoryLoading(false);
    }
  };
  const [scanning, setScanning] = useState(false);
  const runProspectScan = async () => {
    setScanning(true);
    try {
      const q = await api.post(`/api/v1/reports/prospect-scan/${id}`);
      const { command_id, device_id } = q.data;
      const started = Date.now();
      const poll = async (): Promise<void> => {
        const r = await api.get(`/api/v1/devices/${device_id}/command/${command_id}`);
        const st = r.data?.status;
        if (st === "completed") {
          await downloadFile(
            `/api/v1/reports/prospect-assessment/${id}/download.pdf`,
            `Network_Assessment_${customer?.name || "customer"}.pdf`,
            { command_id },
          );
          setScanning(false);
          return;
        }
        if (st === "failed") {
          alert("The domain scan failed on the DC: " + (r.data?.error_message || "unknown error"));
          setScanning(false);
          return;
        }
        if (Date.now() - started > 240_000) {
          alert("The scan is taking longer than expected. Try again shortly.");
          setScanning(false);
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Couldn't start the domain scan");
      setScanning(false);
    }
  };
  const openQbrEmailModal = () => {
    setQbrEmailOverride("");
    setQbrSentInfo("");
    setQbrEmailing(true);
  };
  const sendQbrEmail = async () => {
    setQbrSending(true);
    try {
      const body: Record<string, unknown> = {
        customer_id: Number(id),
        quarter: qbrQuarter,
      };
      if (qbrEmailOverride.trim()) body.to_email = qbrEmailOverride.trim();
      const r = await api.post(`/api/v1/qbr/email`, body);
      setQbrSentInfo(`Sent to ${r.data.recipient} (${r.data.quarter})`);
      setQbrEmailing(false);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to send QBR");
    } finally {
      setQbrSending(false);
    }
  };

  const previewSlaSummary = async () => {
    try {
      const r = await api.get(`/api/v1/sla-summary/preview`, {
        params: { customer_id: id, days: slaSummaryDays },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to generate SLA summary preview");
    }
  };
  const openSlaSummaryModal = () => {
    setSlaSummaryRecipient("");
    setSlaSummaryForce(false);
    setSlaSummaryResult(null);
    setSlaSummarySendOpen(true);
  };
  // ── Compliance evidence package ─────────────────────────────────────────
  const compliancePeriodOptions = useMemo(() => {
    const now = new Date();
    const curQ = Math.floor(now.getMonth() / 3) + 1;
    const curYear = now.getFullYear();
    const out: string[] = [`${curYear}-Q${curQ}`];
    let y = curYear,
      q = curQ;
    for (let i = 0; i < 4; i++) {
      q -= 1;
      if (q < 1) {
        q = 4;
        y -= 1;
      }
      out.push(`${y}-Q${q}`);
    }
    return out;
  }, []);
  const [compliancePeriod, setCompliancePeriod] = useState<string>("");
  useEffect(() => {
    if (!compliancePeriod && compliancePeriodOptions.length > 1) {
      setCompliancePeriod(compliancePeriodOptions[1]);
    }
  }, [compliancePeriod, compliancePeriodOptions]);
  const [complianceSending, setComplianceSending] = useState(false);
  const [complianceEmailOpen, setComplianceEmailOpen] = useState(false);
  const [complianceEmailOverride, setComplianceEmailOverride] = useState("");
  const [complianceSentInfo, setComplianceSentInfo] = useState("");

  const previewCompliance = async () => {
    try {
      const r = await api.get(`/api/v1/compliance/preview`, {
        params: { customer_id: id, period: compliancePeriod },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to generate compliance preview");
    }
  };
  const downloadCompliance = async () => {
    try {
      await downloadFile(
        `/api/v1/compliance/download.zip`,
        `Compliance_${customer?.name || "customer"}_${compliancePeriod}.zip`,
        { customer_id: id, period: compliancePeriod },
      );
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to download compliance ZIP");
    }
  };
  const openComplianceEmailModal = () => {
    setComplianceEmailOverride("");
    setComplianceSentInfo("");
    setComplianceEmailOpen(true);
  };
  const sendComplianceEmail = async () => {
    setComplianceSending(true);
    try {
      const body: Record<string, unknown> = {
        customer_id: Number(id),
        period: compliancePeriod,
      };
      if (complianceEmailOverride.trim())
        body.to_email = complianceEmailOverride.trim();
      const r = await api.post(`/api/v1/compliance/email`, body);
      setComplianceSentInfo(`Sent to ${r.data.recipient} (${r.data.period})`);
      setComplianceEmailOpen(false);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to send compliance package");
    } finally {
      setComplianceSending(false);
    }
  };

  const sendSlaSummary = async () => {
    setSlaSummarySending(true);
    try {
      const body: Record<string, unknown> = {
        customer_id: Number(id),
        days: slaSummaryDays,
        force: slaSummaryForce,
      };
      if (slaSummaryRecipient.trim()) body.to_email = slaSummaryRecipient.trim();
      const r = await api.post(`/api/v1/sla-summary/send`, body);
      if (r.data.sent) {
        setSlaSummaryResult(
          `Sent to ${r.data.recipient} — ${r.data.breach_count} breach(es)`,
        );
      } else if (r.data.reason === "no_breaches") {
        setSlaSummaryResult("Skipped — no breaches in period (enable Force to send anyway).");
      } else if (r.data.reason === "no_recipient") {
        setSlaSummaryResult(
          "Skipped — customer has no contact_email; enter a recipient below.",
        );
      } else {
        setSlaSummaryResult(`Skipped — ${r.data.reason}`);
      }
    } catch (err: any) {
      setSlaSummaryResult(err.response?.data?.detail || "Failed to send SLA summary");
    } finally {
      setSlaSummarySending(false);
    }
  };

  // Credential vault for this customer. The list endpoint redacts the
  // secret — to actually see a password the user clicks "Reveal" which
  // hits POST /reveal, which both decrypts AND writes an AuditLog row.
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [credCategories, setCredCategories] = useState<string[]>([]);
  const [credEditing, setCredEditing] = useState<Credential | null>(null);
  const [credCreating, setCredCreating] = useState(false);
  const [credDraft, setCredDraft] = useState<{
    category: string;
    label: string;
    username: string;
    url: string;
    notes: string;
    secret: string; // empty on edit = leave existing
  }>({ category: "other", label: "", username: "", url: "", notes: "", secret: "" });
  const [credSaving, setCredSaving] = useState(false);
  const [revealed, setRevealed] = useState<CredentialReveal | null>(null);
  const [revealError, setRevealError] = useState("");
  const [revealVisible, setRevealVisible] = useState(false);
  const [copyConfirm, setCopyConfirm] = useState(false);

  const loadCredentials = async () => {
    const [list, cats] = await Promise.all([
      api.get(`/api/v1/credentials`, { params: { customer_id: id } }).then((r) => r.data),
      // Categories endpoint is also gated, so its 403 just means we render the dropdown
      // with the default value. We don't surface that as an error.
      api
        .get(`/api/v1/credentials/meta/categories`)
        .then((r) => r.data)
        .catch(() => []),
    ]);
    setCredentials(list);
    if (cats.length) setCredCategories(cats);
  };

  const loadRunbooks = async () => {
    // Two endpoints: this customer's, plus globals (customer_id IS NULL).
    // Backend list filter is exact-match on customer_id, so we fetch each
    // and merge — keeps the API surface narrow.
    const [mine, globals] = await Promise.all([
      api.get(`/api/v1/runbooks`, { params: { customer_id: id } }).then((r) => r.data),
      api
        .get(`/api/v1/runbooks`)
        .then((r) => r.data.filter((rb: Runbook) => rb.customer_id == null)),
    ]);
    setRunbooks([...mine, ...globals]);
  };
  const [patchPolicySaved, setPatchPolicySaved] = useState(false);

  // Maintenance window
  const [maintWindow, setMaintWindow] = useState<MaintenanceWindow | null>(null);
  const [maintSaved, setMaintSaved] = useState(false);

  // Patch compliance summary for this customer
  const [patchCompliance, setPatchCompliance] = useState<CustomerCompliance | null>(null);

  // Portal users
  const [portalUsers, setPortalUsers] = useState<PortalCustomerUser[]>([]);
  const [inviteForm, setInviteForm] = useState({ email: "", full_name: "" });
  const [inviteError, setInviteError] = useState("");
  const [portalPasswordReveal, setPortalPasswordReveal] = useState<{
    email: string;
    password: string;
  } | null>(null);
  // Customer detail → Portal Access tab: target of the unified reset modal
  const [portalResetTarget, setPortalResetTarget] = useState<PortalCustomerUser | null>(null);

  // Billing form (admin)
  const [billingForm, setBillingForm] = useState({
    default_billable: true,
    hourly_rate_dollars: 0,
    billing_email: "",
    sales_tax_pct: 6.75,
    portal_can_view_invoices: true,
  });
  const [billingSaved, setBillingSaved] = useState("");
  const [reportInfo, setReportInfo] = useState("");
  const [reportError, setReportError] = useState("");

  const load = () => {
    api.get(`/api/v1/customers/${id}`).then((r) => {
      setCustomer(r.data);
      setBillingForm({
        default_billable: r.data.default_billable ?? true,
        hourly_rate_dollars: (r.data.hourly_rate_cents ?? 0) / 100,
        billing_email: r.data.billing_email ?? "",
        sales_tax_pct: r.data.sales_tax_pct ?? 6.75,
        portal_can_view_invoices: r.data.portal_can_view_invoices ?? true,
      });
    });
    api.get(`/api/v1/customers/${id}/groups`).then((r) => setGroups(r.data));
    // customer_id goes through `params`, not the URL string: this page is scoped
    // to the customer in the route, which must win over the header switcher.
    api.get("/api/v1/devices", { params: { customer_id: id } }).then((r) => setDevices(r.data));
    api
      .get("/api/v1/onboarding/list", { params: { customer_id: id } })
      .then((r) => setPackages(r.data));
  };
  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [id]);

  // Load Time data when tab opens / month changes
  useEffect(() => {
    if (tab !== "time") return;
    loadTime();
    // eslint-disable-next-line
  }, [tab, month]);
  const loadTime = async () => {
    const [e, t, running] = await Promise.all([
      api.get("/api/v1/time-entries", { params: { customer_id: id, month } }),
      api.get("/api/v1/time-entries/totals", { params: { customer_id: id, month } }),
      api.get("/api/v1/time-entries", { params: { customer_id: id, running_only: true } }),
    ]);
    setEntries(e.data);
    setTotals(t.data);
    setRunningTimer(running.data[0] || null);
  };

  // Load notes when tab opens
  useEffect(() => {
    if (tab !== "notes") return;
    api
      .get("/api/v1/customer-notes", { params: { customer_id: id } })
      .then((r) => setNotes(r.data));
  }, [tab, id]);

  // Load runbooks when the Runbooks tab opens
  useEffect(() => {
    if (tab !== "runbooks") return;
    loadRunbooks(); /* eslint-disable-next-line */
  }, [tab, id]);

  // Load credentials when the Credentials tab opens
  useEffect(() => {
    if (tab !== "credentials") return;
    loadCredentials(); /* eslint-disable-next-line */
  }, [tab, id]);

  // Load tickets when tab opens / filter changes
  useEffect(() => {
    if (tab !== "tickets") return;
    api
      .get("/api/v1/tickets", { params: { customer_id: id, include_closed: ticketsIncludeClosed } })
      .then((r) => setTickets(r.data));
  }, [tab, id, ticketsIncludeClosed]);

  // Load portal users when tab opens
  useEffect(() => {
    if (tab !== "portal") return;
    loadPortalUsers();
    // eslint-disable-next-line
  }, [tab, id]);

  // Bounce off the billing tab if the user loses access (or never had it).
  useEffect(() => {
    if (tab === "billing" && !canViewBilling) setTab("devices");
  }, [tab, canViewBilling]);

  // Load thresholds + active alerts when Reports tab opens
  useEffect(() => {
    if (tab !== "reports") return;
    Promise.all([
      api.get("/api/v1/alerts/thresholds"),
      api.get(`/api/v1/alerts/thresholds/customers/${id}`),
      api.get("/api/v1/alerts", { params: { customer_id: id } }),
    ]).then(([g, c, a]) => {
      setGlobalThresholds(g.data);
      setCustomerThresholds(c.data);
      setActiveAlerts(a.data);
      // Seed override draft from customer override OR global
      const draft: Record<string, AlertThreshold> = {};
      g.data.forEach((gt: AlertThreshold) => {
        const cust = c.data.find((x: AlertThreshold) => x.metric === gt.metric);
        draft[gt.metric] = cust ? { ...cust } : { ...gt };
      });
      setOverrideDraft(draft);
    });
    // Maintenance window (auto-creates a disabled default on the server side)
    api
      .get(`/api/v1/maintenance-windows/customers/${id}`)
      .then((r) => setMaintWindow(r.data))
      .catch(() => setMaintWindow(null));
    // Patch compliance summary
    api
      .get(`/api/v1/patches/compliance/customers/${id}`)
      .then((r) => setPatchCompliance(r.data))
      .catch(() => setPatchCompliance(null));

    // Patch policy: load global + (try) customer; fall back gracefully when no override exists.
    api.get("/api/v1/patch-policies/global").then((r) => {
      setGlobalPatchPolicy(r.data);
      api
        .get(`/api/v1/patch-policies/customers/${id}`)
        .then((cr) => {
          setCustomerPatchPolicy(cr.data);
          setPatchPolicyDraft({ ...cr.data });
          setPatchPolicyHasOverride(true);
        })
        .catch(() => {
          setCustomerPatchPolicy(null);
          setPatchPolicyDraft({ ...r.data, id: -1, customer_id: Number(id) });
          setPatchPolicyHasOverride(false);
        });
    });

    // SLA policy: same pattern — global first, then look for a customer override.
    api.get("/api/v1/sla-policies/global").then((r) => {
      setGlobalSlaPolicy(r.data);
      api
        .get(`/api/v1/sla-policies/customers/${id}`)
        .then((cr) => {
          setSlaDraft({ ...cr.data });
          setSlaHasOverride(true);
        })
        .catch(() => {
          setSlaDraft({ ...r.data, id: -1, customer_id: Number(id) });
          setSlaHasOverride(false);
        });
    });
  }, [tab, id]);

  const savePatchPolicyOverride = async () => {
    if (!patchPolicyDraft) return;
    try {
      await api.put(`/api/v1/patch-policies/customers/${id}`, {
        auto_approve_severities: patchPolicyDraft.auto_approve_severities,
        auto_approve_categories: patchPolicyDraft.auto_approve_categories,
        decline_severities: patchPolicyDraft.decline_severities,
        decline_categories: patchPolicyDraft.decline_categories,
        approve_kbs: patchPolicyDraft.approve_kbs,
        decline_kbs: patchPolicyDraft.decline_kbs,
      });
      const r = await api.get(`/api/v1/patch-policies/customers/${id}`);
      setCustomerPatchPolicy(r.data);
      setPatchPolicyDraft({ ...r.data });
      setPatchPolicyHasOverride(true);
      setPatchPolicySaved(true);
      setTimeout(() => setPatchPolicySaved(false), 2000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save");
    }
  };

  const [redeployBusy, setRedeployBusy] = useState(false);
  const [redeployMsg, setRedeployMsg] = useState("");
  const redeployOffline = async () => {
    setRedeployBusy(true);
    setRedeployMsg("");
    try {
      const r = await api.post("/api/v1/network-discovery/redeploy-offline", {
        customer_id: Number(id),
      });
      if (r.data.queued > 0) {
        setRedeployMsg(
          `Re-deploy queued for ${r.data.queued} offline device(s) from ${r.data.deployer_hostname}` +
            (r.data.credentialed ? " (using the saved deploy credential)." : " (via the AD runner)."),
        );
      } else if (r.data.reason === "no_offline_targets") {
        setRedeployMsg("No offline devices to re-deploy.");
      } else if (r.data.reason === "no_online_deployer") {
        setRedeployMsg(r.data.detail || "No online on-LAN agent available to run the re-deploy.");
      } else {
        setRedeployMsg("Nothing queued.");
      }
    } catch (e: any) {
      setRedeployMsg(e.response?.data?.detail || "Re-deploy failed.");
    } finally {
      setRedeployBusy(false);
    }
  };

  const saveMaintWindow = async () => {
    if (!maintWindow) return;
    try {
      const r = await api.put(`/api/v1/maintenance-windows/customers/${id}`, {
        enabled: maintWindow.enabled,
        days_of_week: maintWindow.days_of_week,
        start_time: maintWindow.start_time,
        end_time: maintWindow.end_time,
        timezone: maintWindow.timezone,
        allow_reboot: maintWindow.allow_reboot,
        reboot_deferral_seconds: maintWindow.reboot_deferral_seconds,
        reboot_message: maintWindow.reboot_message,
      });
      setMaintWindow(r.data);
      setMaintSaved(true);
      setTimeout(() => setMaintSaved(false), 2000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save");
    }
  };

  const clearPatchPolicyOverride = async () => {
    if (
      !window.confirm("Remove this customer's patch policy override and inherit the global policy?")
    )
      return;
    await api.delete(`/api/v1/patch-policies/customers/${id}`);
    setCustomerPatchPolicy(null);
    setPatchPolicyHasOverride(false);
    if (globalPatchPolicy)
      setPatchPolicyDraft({ ...globalPatchPolicy, id: -1, customer_id: Number(id) });
  };

  const saveSlaOverride = async () => {
    if (!slaDraft) return;
    const { id: _id, customer_id: _cid, updated_at: _ua, ...minutes } = slaDraft;
    void _id;
    void _cid;
    void _ua;
    try {
      await api.put(`/api/v1/sla-policies/customers/${id}`, minutes);
      const r = await api.get(`/api/v1/sla-policies/customers/${id}`);
      setSlaDraft({ ...r.data });
      setSlaHasOverride(true);
      setSlaSaved(true);
      setTimeout(() => setSlaSaved(false), 2000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save SLA override");
    }
  };

  const clearSlaOverride = async () => {
    if (!window.confirm("Remove this customer's SLA override and inherit the global policy?"))
      return;
    await api.delete(`/api/v1/sla-policies/customers/${id}`);
    setSlaHasOverride(false);
    if (globalSlaPolicy) setSlaDraft({ ...globalSlaPolicy, id: -1, customer_id: Number(id) });
  };

  // ── Runbook editor handlers (used by the Runbooks tab) ──────────────────
  const openRbCreate = () => {
    setRbDraft({
      customer_id: Number(id), // default scope = this customer; editor can flip to global
      visibility: "internal",
      title: "",
      description: "",
      steps: [{ title: "", body: "" }],
    });
    setRbEditing(null);
    setRbCreating(true);
  };
  const openRbEdit = (rb: Runbook) => {
    setRbDraft({
      customer_id: rb.customer_id,
      visibility: rb.visibility,
      title: rb.title,
      description: rb.description || "",
      steps: rb.steps.length ? rb.steps : [{ title: "", body: "" }],
    });
    setRbEditing(rb);
    setRbCreating(false);
  };
  const closeRbEditor = () => {
    setRbEditing(null);
    setRbCreating(false);
  };
  const saveRunbook = async () => {
    if (!rbDraft.title.trim()) {
      alert("Title is required");
      return;
    }
    setRbSaving(true);
    try {
      const payload = {
        ...rbDraft,
        description: rbDraft.description || null,
        steps: rbDraft.steps.filter((s) => s.title.trim() || s.body.trim()),
      };
      if (rbEditing) await api.put(`/api/v1/runbooks/${rbEditing.id}`, payload);
      else await api.post(`/api/v1/runbooks`, payload);
      closeRbEditor();
      loadRunbooks();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save runbook");
    } finally {
      setRbSaving(false);
    }
  };
  const deleteRunbook = async (rb: Runbook) => {
    if (!window.confirm(`Delete runbook "${rb.title}"? This cannot be undone.`)) return;
    await api.delete(`/api/v1/runbooks/${rb.id}`);
    loadRunbooks();
  };
  const setRbStep = (idx: number, patch: Partial<RunbookStep>) => {
    setRbDraft({
      ...rbDraft,
      steps: rbDraft.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    });
  };
  const addRbStep = () =>
    setRbDraft({ ...rbDraft, steps: [...rbDraft.steps, { title: "", body: "" }] });
  const removeRbStep = (idx: number) =>
    setRbDraft({ ...rbDraft, steps: rbDraft.steps.filter((_, i) => i !== idx) });
  const moveRbStep = (idx: number, dir: -1 | 1) => {
    const next = [...rbDraft.steps];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setRbDraft({ ...rbDraft, steps: next });
  };

  // ── Credential vault handlers ────────────────────────────────────────────
  const openCredCreate = () => {
    setCredDraft({
      category: "other",
      label: "",
      username: "",
      url: "",
      notes: "",
      secret: "",
    });
    setCredEditing(null);
    setCredCreating(true);
  };
  const openCredEdit = (c: Credential) => {
    setCredDraft({
      category: c.category,
      label: c.label,
      username: c.username || "",
      url: c.url || "",
      notes: c.notes || "",
      secret: "", // leave blank to keep existing
    });
    setCredEditing(c);
    setCredCreating(false);
  };
  const closeCredEditor = () => {
    setCredEditing(null);
    setCredCreating(false);
  };
  const generateCredPassword = () => {
    // 20-char alnum + safe-symbol password. Uses crypto.getRandomValues
    // (browser CSPRNG) so it's suitable for real credentials, not just
    // demo data.
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*-_=+";
    const bytes = new Uint8Array(20);
    window.crypto.getRandomValues(bytes);
    const pw = Array.from(bytes, (b) => alphabet[b % alphabet.length]).join("");
    setCredDraft({ ...credDraft, secret: pw });
  };
  const saveCredential = async () => {
    if (!credDraft.label.trim()) {
      alert("Label is required");
      return;
    }
    if (!credEditing && !credDraft.secret) {
      alert("Secret is required for new credentials");
      return;
    }
    setCredSaving(true);
    try {
      const base = {
        customer_id: Number(id),
        category: credDraft.category,
        label: credDraft.label.trim(),
        username: credDraft.username || null,
        url: credDraft.url || null,
        notes: credDraft.notes || null,
      };
      if (credEditing) {
        const payload: Record<string, unknown> = { ...base };
        if (credDraft.secret) payload.secret = credDraft.secret;
        await api.put(`/api/v1/credentials/${credEditing.id}`, payload);
      } else {
        await api.post(`/api/v1/credentials`, { ...base, secret: credDraft.secret });
      }
      closeCredEditor();
      loadCredentials();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save credential");
    } finally {
      setCredSaving(false);
    }
  };
  const deleteCredential = async (c: Credential) => {
    if (!window.confirm(`Delete credential "${c.label}"? This cannot be undone.`)) return;
    await api.delete(`/api/v1/credentials/${c.id}`);
    loadCredentials();
  };
  const revealCredential = async (c: Credential) => {
    setRevealError("");
    setRevealVisible(false);
    setCopyConfirm(false);
    try {
      const r = await api.post(`/api/v1/credentials/${c.id}/reveal`);
      setRevealed(r.data);
    } catch (err: any) {
      setRevealError(err.response?.data?.detail || "Failed to reveal credential");
      setRevealed({
        id: c.id,
        label: c.label,
        username: c.username,
        secret: "",
        revealed_at: new Date().toISOString(),
      });
    }
  };
  const copyRevealed = async () => {
    if (!revealed?.secret) return;
    try {
      await navigator.clipboard.writeText(revealed.secret);
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 2000);
    } catch {
      // Some browsers block clipboard outside HTTPS; tell the user instead of failing silently.
      alert("Couldn't copy automatically — select the secret and copy manually.");
    }
  };
  const closeReveal = () => {
    setRevealed(null);
    setRevealVisible(false);
    setRevealError("");
  };
  const formatCategoryLabel = (cat: string) =>
    cat
      .split("_")
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(" ");

  const saveOverride = async (metric: string) => {
    const d = overrideDraft[metric];
    if (!d) return;
    try {
      await api.put(`/api/v1/alerts/thresholds/customers/${id}/${metric}`, {
        info_pct: d.info_pct,
        warn_pct: d.warn_pct,
        critical_pct: d.critical_pct,
        re_alert_hours: d.re_alert_hours,
      });
      setOverrideSaved(metric);
      const c = await api.get(`/api/v1/alerts/thresholds/customers/${id}`);
      setCustomerThresholds(c.data);
      setTimeout(() => setOverrideSaved(null), 2000);
    } catch (err: any) {
      alert(err.response?.data?.detail || "Failed to save");
    }
  };

  const clearOverride = async (metric: string) => {
    if (!window.confirm("Remove the override and inherit the global threshold for this metric?"))
      return;
    await api.delete(`/api/v1/alerts/thresholds/customers/${id}/${metric}`);
    const c = await api.get(`/api/v1/alerts/thresholds/customers/${id}`);
    setCustomerThresholds(c.data);
    const g = globalThresholds.find((x) => x.metric === metric);
    if (g) setOverrideDraft({ ...overrideDraft, [metric]: { ...g } });
  };

  const acknowledgeAlert = async (a: Alert) => {
    await api.post(`/api/v1/alerts/${a.id}/acknowledge`);
    const r = await api.get("/api/v1/alerts", { params: { customer_id: id } });
    setActiveAlerts(r.data);
  };
  const loadPortalUsers = () =>
    api.get(`/api/v1/customers/${id}/portal-users`).then((r) => setPortalUsers(r.data));

  const generatePackage = async (os_type: "windows" | "macos" | "linux") => {
    await api.post("/api/v1/onboarding", { customer_id: Number(id), os_type });
    load();
  };
  const deletePackage = async (pkgId: number) => {
    if (
      !window.confirm(
        "Delete this installer package? The download link stops working; devices that already installed keep running normally."
      )
    )
      return;
    await api.delete(`/api/v1/onboarding/${pkgId}`);
    load();
  };
  // One-click Group Policy deploy: download a script the Domain Admin runs once
  // on a DC to push the agent to every domain computer (no per-device push).
  const downloadGpoScript = async () => {
    try {
      const r = await api.get("/api/v1/onboarding/gpo-script", {
        params: { customer_id: Number(id), format: "cmd" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([r.data], { type: "application/octet-stream" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "AeroGauge-GPO-Deploy.cmd";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.detail || e?.message || "Could not generate the GPO script");
    }
  };

  // ── Time actions ─────────────────────────────────────────────────────────
  const startTimer = async () => {
    if (!customer) return;
    await api.post("/api/v1/time-entries/start", {
      customer_id: customer.id,
      description: newEntry.description || null,
      billable: newEntry.billable,
    });
    setNewEntry({ ...newEntry, description: "" });
    loadTime();
  };
  const stopTimer = async () => {
    if (!runningTimer) return;
    await api.post(`/api/v1/time-entries/${runningTimer.id}/stop`);
    loadTime();
  };
  const addManual = async () => {
    if (!customer) return;
    const start = new Date(newEntry.started_at);
    const minutes = Math.round(newEntry.duration_hours * 60);
    const end = new Date(start.getTime() + minutes * 60_000);
    await api.post("/api/v1/time-entries", {
      customer_id: customer.id,
      description: newEntry.description || null,
      started_at: start.toISOString(),
      ended_at: end.toISOString(),
      duration_minutes: minutes,
      billable: newEntry.billable,
    });
    setNewEntry({ ...newEntry, description: "", duration_hours: 0.5 });
    loadTime();
  };
  const toggleBillable = async (e: TimeEntry) => {
    await api.put(`/api/v1/time-entries/${e.id}`, { billable: !e.billable });
    loadTime();
  };
  const deleteEntry = async (e: TimeEntry) => {
    if (!window.confirm("Delete this time entry?")) return;
    await api.delete(`/api/v1/time-entries/${e.id}`);
    loadTime();
  };
  const beginEditEntry = (e: TimeEntry) => {
    if (!e.ended_at) return;
    const d = new Date(e.started_at);
    const pad = (n: number) => String(n).padStart(2, "0");
    setEditForm({
      description: e.description || "",
      started_at: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
      duration_hours: (e.duration_minutes || 0) / 60,
    });
    setEditingEntryId(e.id);
  };
  const cancelEditEntry = () => setEditingEntryId(null);
  const saveEditEntry = async () => {
    if (editingEntryId == null) return;
    const start = new Date(editForm.started_at);
    const minutes = Math.round(editForm.duration_hours * 60);
    const end = new Date(start.getTime() + minutes * 60_000);
    await api.put(`/api/v1/time-entries/${editingEntryId}`, {
      description: editForm.description || null,
      started_at: start.toISOString(),
      ended_at: end.toISOString(),
      duration_minutes: minutes,
    });
    setEditingEntryId(null);
    loadTime();
  };

  // ── Notes actions ────────────────────────────────────────────────────────
  const submitNote = async () => {
    const body = newNote.trim();
    if (!body) return;
    await api.post(`/api/v1/customer-notes/${id}`, { body });
    setNewNote("");
    api
      .get("/api/v1/customer-notes", { params: { customer_id: id } })
      .then((r) => setNotes(r.data));
  };
  const deleteNote = async (n: CustomerNote) => {
    if (!window.confirm("Delete this note?")) return;
    await api.delete(`/api/v1/customer-notes/${n.id}`);
    api
      .get("/api/v1/customer-notes", { params: { customer_id: id } })
      .then((r) => setNotes(r.data));
  };
  const beginEditNote = (n: CustomerNote) => {
    setEditingNoteId(n.id);
    setEditNoteBody(n.body);
  };
  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteBody("");
  };
  const saveEditNote = async () => {
    if (editingNoteId == null) return;
    const body = editNoteBody.trim();
    if (!body) return;
    await api.put(`/api/v1/customer-notes/${editingNoteId}`, { body });
    setEditingNoteId(null);
    setEditNoteBody("");
    api
      .get("/api/v1/customer-notes", { params: { customer_id: id } })
      .then((r) => setNotes(r.data));
  };

  // ── Billing actions ──────────────────────────────────────────────────────
  const saveBilling = async () => {
    setBillingSaved("");
    await api.put(`/api/v1/customers/${id}`, {
      default_billable: billingForm.default_billable,
      hourly_rate_cents: Math.round(billingForm.hourly_rate_dollars * 100),
      billing_email: billingForm.billing_email || null,
      sales_tax_pct: billingForm.sales_tax_pct,
      portal_can_view_invoices: billingForm.portal_can_view_invoices,
    });
    setBillingSaved("Saved.");
    load();
    setTimeout(() => setBillingSaved(""), 2500);
  };
  const sendReportPreview = async () => {
    setReportError("");
    setReportInfo("");
    if (
      !window.confirm(
        `Send the prior month's statement to ${customer?.billing_email || customer?.contact_email || "this customer"} from the appliance's no-reply address now?`,
      )
    )
      return;
    try {
      const r = await api.post(`/api/v1/billing/report/preview/${id}`);
      setReportInfo(
        `Sent to ${r.data.recipient}. ${r.data.entry_count} entries, ${fmtCents(r.data.amount_cents)} billable.`,
      );
    } catch (err: any) {
      setReportError(err.response?.data?.detail || "Failed to send report");
    }
  };

  const monthOptions = useMemo(() => {
    const out: string[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return out;
  }, []);

  if (!customer) return <div className="text-gray-400 p-8">Loading...</div>;

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: "devices", label: `Devices (${devices.length})` },
    { key: "groups", label: `Groups (${groups.length})` },
    { key: "onboarding", label: "Onboarding" },
    { key: "tickets", label: "Tickets" },
    { key: "ad", label: "Active Directory" },
    { key: "time", label: "Time" },
    { key: "notes", label: "Notes" },
    ...(canViewBilling ? [{ key: "billing" as Tab, label: "Billing" }] : []),
    { key: "reports", label: "Reports" },
    { key: "runbooks", label: "Runbooks" },
    { key: "credentials", label: "Credentials" },
    ...(isAdmin ? [{ key: "cloud" as Tab, label: "Cloud" }] : []),
    { key: "meraki_licenses", label: "Licenses & renewals" },
    // Remote Desktop is now provided by the co-hosted Devolutions Gateway (no
    // per-customer config). The old per-customer MeshCentral config tab is hidden
    // to avoid confusion; the MeshCentralTab component + API remain for the
    // optional MeshCentral add-on. Re-add this entry to expose it again.
    // { key: "meshcentral", label: "Remote Desktop" },
    { key: "portal", label: "Portal Access" },
  ];

  const invitePortalUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError("");
    if (!inviteForm.email.trim()) {
      setInviteError("Email required");
      return;
    }
    try {
      const r = await api.post(`/api/v1/customers/${id}/portal-users`, {
        email: inviteForm.email.trim(),
        full_name: inviteForm.full_name.trim() || null,
      });
      setPortalPasswordReveal({ email: r.data.user.email, password: r.data.initial_password });
      setInviteForm({ email: "", full_name: "" });
      loadPortalUsers();
    } catch (err: any) {
      setInviteError(err.response?.data?.detail || "Failed to invite");
    }
  };

  const togglePortalUser = async (u: PortalCustomerUser) => {
    await api.put(`/api/v1/customers/portal-users/${u.id}`, { is_active: !u.is_active });
    loadPortalUsers();
  };

  const togglePortalUserInvoices = async (u: PortalCustomerUser) => {
    await api.put(`/api/v1/customers/portal-users/${u.id}`, {
      can_view_invoices: !u.can_view_invoices,
    });
    loadPortalUsers();
  };

  const handlePortalResetSubmit = async (
    mode: "auto" | "custom",
    customPassword?: string,
  ): Promise<string | null> => {
    if (!portalResetTarget) return null;
    const body = mode === "custom" ? { new_password: customPassword } : {};
    const r = await api.post(
      `/api/v1/customers/portal-users/${portalResetTarget.id}/reset-password`,
      body,
    );
    loadPortalUsers();
    return r.data.new_password;
  };

  const deletePortalUser = async (u: PortalCustomerUser) => {
    if (!window.confirm(`Delete portal user ${u.email}? They will lose access immediately.`))
      return;
    await api.delete(`/api/v1/customers/portal-users/${u.id}`);
    loadPortalUsers();
  };

  return (
    <div>
      <button
        onClick={() => navigate("/customers")}
        className="text-sm text-blue-600 hover:underline mb-4 inline-block"
      >
        &larr; Back to Customers
      </button>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 font-bold text-2xl">
            {customer.name.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{customer.name}</h1>
            {customer.domain && <p className="text-sm text-gray-500 dark:text-gray-400">{customer.domain}</p>}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-center px-4 py-2 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <div className="text-xl font-bold text-gray-800 dark:text-gray-200">{customer.device_count}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Devices</div>
            </div>
            <div className="text-center px-4 py-2 bg-green-50 rounded-lg">
              <div className="text-xl font-bold text-green-600">{customer.online_count}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Online</div>
            </div>
            <ActionButton onAction={() => openAssessment()} variant="raw" className="text-sm bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700" title="Assessment PDF from installed agents (managed fleet health)" pendingLabel="Working…">
              📄 Assessment report
            </ActionButton>
            <button
              onClick={openInventory}
              disabled={inventoryLoading}
              className="text-sm bg-slate-600 text-white px-4 py-2 rounded-lg hover:bg-slate-700 disabled:opacity-50"
              title="Inventory PDF: every endpoint + network device with an AI summary of what it does"
            >
              {inventoryLoading ? "Generating…" : "📋 Inventory report"}
            </button>
            <button
              onClick={runProspectScan}
              disabled={scanning}
              className="text-sm bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
              title="Scan the whole domain from the DC agent (no per-machine agents) → prospect/sales risk report"
            >
              {scanning ? "Scanning domain…" : "🔍 Network scan (prospect)"}
            </button>
            {isAdmin && (
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      `Delete ${customer.name}? This removes the customer from your active ` +
                        `customer list and frees its license seat. Historical records are ` +
                        `retained and can be restored by your provider if needed. Continue?`,
                    )
                  )
                    return;
                  try {
                    await api.delete(`/api/v1/customers/${id}`);
                    navigate("/customers");
                  } catch {
                    window.alert("The customer could not be deleted. Please try again.");
                  }
                }}
                className="text-sm border border-red-300 text-red-700 rounded-lg px-3 py-1.5 hover:bg-red-50 self-center"
                title="Remove this customer from active views"
              >
                🗑 Delete customer
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-300">
          {customer.primary_contact && (
            <div>
              <span className="font-medium">Contact:</span> {customer.primary_contact}
            </div>
          )}
          {customer.contact_email && (
            <div>
              <span className="font-medium">Email:</span> {customer.contact_email}
            </div>
          )}
          {customer.contact_phone && (
            <div>
              <span className="font-medium">Phone:</span> {customer.contact_phone}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b dark:border-gray-700">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.key
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "devices" && (
        <>
          {isAdmin &&
            devices.filter((d) => d.status !== "online").length > 0 && (
              <div className="mb-4 flex items-center justify-between gap-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
                <span>
                  ⚠ {devices.filter((d) => d.status !== "online").length} device(s) offline. If their
                  agents crashed or were removed, re-deploy pushes a fresh agent from an online
                  on-LAN machine (uses the saved deploy credential, or the AD runner).
                  {redeployMsg && (
                    <span className="block mt-1 font-medium text-amber-800 dark:text-amber-200">
                      {redeployMsg}
                    </span>
                  )}
                </span>
                <button
                  onClick={redeployOffline}
                  disabled={redeployBusy}
                  className="shrink-0 text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
                  title="Re-deploy the agent to this customer's offline devices from an online on-LAN collector"
                >
                  {redeployBusy ? "Queuing…" : "⤓ Re-deploy agent to offline devices"}
                </button>
              </div>
            )}

          {/* The outcome of the last deploy, per machine. Previously the
              button said "queued for N devices" and the technician never
              learned which ones worked - the results existed in the database
              and no screen rendered them. */}
          {isAdmin && <DeployResultPanel customerId={Number(id)} />}
          <DevicesTab
            devices={devices}
            isAdmin={isAdmin}
            onChanged={() =>
              api
                .get("/api/v1/devices", { params: { customer_id: id } })
                .then((r) => setDevices(r.data))
            }
          />
        </>
      )}

      {tab === "groups" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {groups.map((g) => (
            <div key={g.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{g.name}</h3>
              {g.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{g.description}</p>}
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">{g.device_count} device(s)</div>
            </div>
          ))}
        </div>
      )}

      {tab === "onboarding" && (
        <div>
          <OnboardingPreflightBanner />
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-4 mb-4 flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-900 dark:text-blue-200">
                Deploy to a whole Windows domain in one click
              </div>
              <p className="text-xs text-blue-800 dark:text-blue-300 mt-1 leading-snug">
                Downloads a script the customer's Domain Admin runs once on a Domain
                Controller. It creates a Group Policy that installs the agent on every
                domain computer at next reboot — no per-device push, no WinRM. Idempotent
                (skips machines that already have it).
              </p>
            </div>
            <ActionButton onAction={() => downloadGpoScript()} variant="raw" className="shrink-0 bg-blue-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-blue-700" pendingLabel="Preparing…">
              Deploy via Group Policy
            </ActionButton>
          </div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Or generate a single-machine OS-specific installer. The download is
              pre-associated to this customer profile — no setup needed at install time.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => generatePackage("windows")}
                className="bg-blue-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-blue-700"
              >
                Windows installer
              </button>
              <button
                onClick={() => generatePackage("macos")}
                className="bg-slate-700 text-white text-sm px-3 py-2 rounded-lg hover:bg-slate-800"
              >
                macOS installer
              </button>
              <button
                onClick={() => generatePackage("linux")}
                className="bg-slate-700 text-white text-sm px-3 py-2 rounded-lg hover:bg-slate-800"
              >
                Linux installer
              </button>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="OS" sortKey="os_type" sort={pkgSort.sort} onSort={pkgSort.toggle} />
                  <SortableTh label="Created" sortKey="created_at" sort={pkgSort.sort} onSort={pkgSort.toggle} />
                  <SortableTh label="Downloads" sortKey="download_count" sort={pkgSort.sort} onSort={pkgSort.toggle} />
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortRows(packages, pkgSort.sort, (p, key) => {
                  switch (key) {
                    case "created_at":
                      return p.created_at ? Date.parse(p.created_at) : 0;
                    case "download_count":
                      return p.download_count;
                    default:
                      return (p.os_type || "").toLowerCase();
                  }
                }).map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300 capitalize">{p.os_type || "windows"}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                      {formatDate(p.created_at)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{p.download_count}</td>
                    <td className="px-4 py-3">
                      <a
                        href={p.download_url}
                        className="text-blue-600 hover:underline text-xs font-medium"
                      >
                        Download ZIP
                      </a>
                      <button
                        onClick={() => deletePackage(p.id)}
                        className="ml-3 text-red-600 hover:underline text-xs font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {packages.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                      No packages yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "tickets" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={ticketsIncludeClosed}
                onChange={(e) => setTicketsIncludeClosed(e.target.checked)}
              />
              Include closed
            </label>
            {canEdit && (
              <Link
                to={`/tickets`}
                state={{ customer_id: customer.id }}
                className="text-xs text-blue-600 hover:underline"
              >
                Open all tickets →
              </Link>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="#" sortKey="id" sort={ticketSort.sort} onSort={ticketSort.toggle} />
                  <SortableTh label="Title" sortKey="title" sort={ticketSort.sort} onSort={ticketSort.toggle} />
                  <SortableTh label="Priority" sortKey="priority" sort={ticketSort.sort} onSort={ticketSort.toggle} />
                  <SortableTh label="Status" sortKey="status" sort={ticketSort.sort} onSort={ticketSort.toggle} />
                  <SortableTh label="Assignee" sortKey="assigned_to_username" sort={ticketSort.sort} onSort={ticketSort.toggle} />
                  <SortableTh label="Time" sortKey="time_logged_minutes" sort={ticketSort.sort} onSort={ticketSort.toggle} align="right" />
                  <SortableTh label="Updated" sortKey="updated_at" sort={ticketSort.sort} onSort={ticketSort.toggle} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {tickets.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                      No tickets for this customer.
                    </td>
                  </tr>
                )}
                {sortRows(tickets, ticketSort.sort, (t, key) => {
                  switch (key) {
                    case "id":
                      return t.id;
                    case "priority":
                      return TICKET_PRIORITY_RANK[t.priority] ?? -1;
                    case "status":
                      return TICKET_STATUS_RANK[t.status] ?? -1;
                    case "assigned_to_username":
                      return (t.assigned_to_username || "").toLowerCase();
                    case "time_logged_minutes":
                      return t.time_logged_minutes;
                    case "updated_at":
                      return t.updated_at ? Date.parse(t.updated_at) : 0;
                    default:
                      return (t.title || "").toLowerCase();
                  }
                }).map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-2 font-mono text-xs text-gray-500 dark:text-gray-400">#{t.id}</td>
                    <td className="px-4 py-2">
                      <Link to={`/tickets/${t.id}`} className="text-blue-600 hover:underline">
                        {t.title}
                      </Link>
                    </td>
                    <td className="px-4 py-2 capitalize text-xs">{t.priority}</td>
                    <td className="px-4 py-2 text-xs">
                      {t.status.replace("_", " ")}
                      {t.status === "on_hold" && t.hold_category_name
                        ? ` · ${t.hold_category_name}`
                        : ""}
                    </td>
                    <td className="px-4 py-2 text-xs">{t.assigned_to_username || "—"}</td>
                    <td className="px-4 py-2 text-xs text-right">
                      {t.time_logged_minutes > 0
                        ? `${(t.time_logged_minutes / 60).toFixed(1)} hr`
                        : "—"}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-400">
                      {formatDateTime(t.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "ad" && <ADUsers customerOverride={Number(id)} hideTitle />}

      {tab === "time" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            <SummaryCard
              label="Billable"
              value={fmtMinutes(totals?.billable_minutes ?? 0)}
              color="text-green-600"
              bg="bg-green-50"
            />
            <SummaryCard
              label="Non-Billable"
              value={fmtMinutes(totals?.non_billable_minutes ?? 0)}
              color="text-gray-700 dark:text-gray-300"
              bg="bg-gray-50 dark:bg-gray-900/50"
            />
            <SummaryCard
              label="Subtotal"
              value={fmtCents(totals?.billable_amount_cents ?? 0)}
              color="text-gray-700 dark:text-gray-300"
              bg="bg-gray-50 dark:bg-gray-900/50"
            />
            <SummaryCard
              label={`Sales Tax (${(totals?.tax_pct ?? 0).toFixed(2)}%)`}
              value={fmtCents(totals?.tax_amount_cents ?? 0)}
              color="text-gray-700 dark:text-gray-300"
              bg="bg-gray-50 dark:bg-gray-900/50"
            />
            <SummaryCard
              label="Total Due"
              value={fmtCents(totals?.total_with_tax_cents ?? 0)}
              color="text-blue-700"
              bg="bg-blue-50"
            />
            <SummaryCard
              label="Entries"
              value={String(totals?.entry_count ?? 0)}
              color="text-gray-700 dark:text-gray-300"
              bg="bg-gray-50 dark:bg-gray-900/50"
            />
          </div>

          {canEdit && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {runningTimer ? `Timer running on ${customer.name}` : "Log time"}
                </h3>
                <div className="ml-auto flex items-center gap-2">
                  <select
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() =>
                      downloadFile("/api/v1/exports/time-entries.pdf", "time-entries.pdf", {
                        customer_id: id,
                        month,
                      })
                    }
                    className="border dark:border-gray-600 dark:text-gray-200 text-xs px-3 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    Download PDF
                  </button>
                  <button
                    onClick={() =>
                      downloadFile("/api/v1/exports/time-entries.csv", "time-entries.csv", {
                        customer_id: id,
                        month,
                      })
                    }
                    className="border dark:border-gray-600 dark:text-gray-200 text-xs px-3 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  >
                    Download CSV
                  </button>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 items-center">
                <input
                  placeholder="Description (what work was done)"
                  value={newEntry.description}
                  onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
                  className="flex-1 min-w-[200px] border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                />
                {!runningTimer && (
                  <>
                    <input
                      type="datetime-local"
                      value={newEntry.started_at}
                      onChange={(e) => setNewEntry({ ...newEntry, started_at: e.target.value })}
                      className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-2 text-sm"
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0.01}
                        max={24}
                        step={0.25}
                        value={newEntry.duration_hours}
                        onChange={(e) =>
                          setNewEntry({ ...newEntry, duration_hours: Number(e.target.value) })
                        }
                        className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-2 text-sm w-20"
                        title="Duration in hours (e.g. 1 = 1 hr, 0.5 = 30 min)"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">hr</span>
                    </div>
                  </>
                )}
                <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={newEntry.billable}
                    onChange={(e) => setNewEntry({ ...newEntry, billable: e.target.checked })}
                  />
                  Billable
                </label>
                {runningTimer ? (
                  <ActionButton onAction={() => stopTimer()} variant="raw" className="bg-red-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-red-700" pendingLabel="Working…">
                    Stop Timer
                  </ActionButton>
                ) : (
                  <>
                    <ActionButton onAction={() => addManual()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700" pendingLabel="Adding…">
                      Add Entry
                    </ActionButton>
                    <ActionButton onAction={() => startTimer()} variant="raw" className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40" pendingLabel="Working…">
                      Start Timer
                    </ActionButton>
                  </>
                )}
              </div>
              {runningTimer && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Started {formatDateTime(runningTimer.started_at)}. Click "Stop Timer"
                  to record the duration.
                </p>
              )}
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Date" sortKey="started_at" sort={timeSort.sort} onSort={timeSort.toggle} />
                  <SortableTh label="Tech" sortKey="user_username" sort={timeSort.sort} onSort={timeSort.toggle} />
                  <SortableTh label="Description" sortKey="description" sort={timeSort.sort} onSort={timeSort.toggle} />
                  <SortableTh label="Duration" sortKey="duration_minutes" sort={timeSort.sort} onSort={timeSort.toggle} align="right" />
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    <button
                      type="button"
                      onClick={() => timeSort.toggle("billable")}
                      className="group inline-flex items-center gap-1 hover:text-gray-800 dark:hover:text-gray-100 transition-colors uppercase"
                      title="Sort by Billable"
                    >
                      Billable
                      <span
                        className={
                          timeSort.sort.key === "billable"
                            ? "text-gray-700 dark:text-gray-200"
                            : "text-gray-300 dark:text-gray-600 group-hover:text-gray-400"
                        }
                      >
                        {timeSort.sort.key === "billable" ? (timeSort.sort.dir === "asc" ? "▲" : "▼") : "↕"}
                      </span>
                    </button>
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Line Total
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                      No time entries for this month.
                    </td>
                  </tr>
                )}
                {sortRows(entries, timeSort.sort, (e, key) => {
                  switch (key) {
                    case "user_username":
                      return (e.user_username || "").toLowerCase();
                    case "description":
                      return (e.description || "").toLowerCase();
                    case "duration_minutes":
                      return e.duration_minutes || 0;
                    case "billable":
                      return e.billable ? 1 : 0;
                    default:
                      return e.started_at ? Date.parse(e.started_at) : 0;
                  }
                }).map((e) => {
                  const running = !e.ended_at;
                  const editing = editingEntryId === e.id;
                  const projectedCents = editing
                    ? Math.round((editForm.duration_hours || 0) * (e.rate_cents_snapshot || 0))
                    : 0;
                  const lineCents =
                    e.billable && e.duration_minutes
                      ? Math.round((e.duration_minutes * e.rate_cents_snapshot) / 60)
                      : 0;
                  return (
                    <tr key={e.id} className={running ? "bg-yellow-50" : editing ? "bg-blue-50" : ""}>
                      <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {editing ? (
                          <input
                            type="datetime-local"
                            value={editForm.started_at}
                            onChange={(ev) =>
                              setEditForm({ ...editForm, started_at: ev.target.value })
                            }
                            className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-1 text-xs"
                          />
                        ) : (
                          formatDateTime(e.started_at)
                        )}
                      </td>
                      <td className="px-4 py-2">{e.user_username || "—"}</td>
                      <td className="px-4 py-2">
                        {editing ? (
                          <input
                            type="text"
                            value={editForm.description}
                            onChange={(ev) =>
                              setEditForm({ ...editForm, description: ev.target.value })
                            }
                            className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                          />
                        ) : (
                          e.description || "—"
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {running ? (
                          <span className="text-xs text-yellow-700">running…</span>
                        ) : editing ? (
                          <div className="inline-flex items-center gap-1">
                            <input
                              type="number"
                              min={0.01}
                              max={24}
                              step={0.25}
                              value={editForm.duration_hours}
                              onChange={(ev) =>
                                setEditForm({
                                  ...editForm,
                                  duration_hours: Number(ev.target.value),
                                })
                              }
                              className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-1 text-sm w-20 text-right"
                              title="Duration in hours (e.g. 1 = 1 hr, 0.5 = 30 min)"
                            />
                            <span className="text-xs text-gray-500 dark:text-gray-400">hr</span>
                          </div>
                        ) : (
                          fmtMinutes(e.duration_minutes)
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {canEdit ? (
                          <input
                            type="checkbox"
                            checked={e.billable}
                            onChange={() => toggleBillable(e)}
                            disabled={editing}
                            title="Toggle billable"
                          />
                        ) : (
                          <span>{e.billable ? "Yes" : "No"}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {editing
                          ? e.billable
                            ? fmtCents(projectedCents)
                            : "—"
                          : e.billable && !running
                            ? fmtCents(lineCents)
                            : "—"}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {canEdit && editing && (
                          <>
                            <ActionButton onAction={() => saveEditEntry()} variant="raw" className="text-xs text-blue-600 hover:underline mr-3" pendingLabel="Saving…">
                              Save
                            </ActionButton>
                            <button
                              onClick={cancelEditEntry}
                              className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {canEdit && !editing && !running && (
                          <>
                            <button
                              onClick={() => beginEditEntry(e)}
                              className="text-xs text-blue-600 hover:underline mr-3"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteEntry(e)}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "notes" && (
        <div className="space-y-4">
          {canEdit && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
              <textarea
                placeholder="Add a note about this customer (phone call, site visit, decision, etc.)…"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={3}
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              />
              <div className="flex justify-end mt-2">
                <ActionButton onAction={() => submitNote()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700" pendingLabel="Working…">
                  Add Note
                </ActionButton>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {notes.length === 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 text-center text-gray-400">
                No notes yet.
              </div>
            )}
            {notes.map((n) => {
              const editing = editingNoteId === n.id;
              const canEditThisNote =
                isAdmin || (canEdit && n.user_id != null && n.user_id === user?.id);
              return (
                <div
                  key={n.id}
                  className={
                    "rounded-xl border shadow-sm p-4 " +
                    (editing
                      ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                      : "bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700")
                  }
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      <strong className="text-gray-700 dark:text-gray-300">{n.author_username || "—"}</strong>
                      {" · "}
                      {formatDateTime(n.created_at)}
                    </div>
                    <div className="flex items-center gap-3">
                      {editing ? (
                        <>
                          <ActionButton onAction={() => saveEditNote()} variant="raw" className="text-xs text-blue-600 hover:underline" pendingLabel="Saving…">
                            Save
                          </ActionButton>
                          <button
                            onClick={cancelEditNote}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {canEditThisNote && (
                            <button
                              onClick={() => beginEditNote(n)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Edit
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              onClick={() => deleteNote(n)}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {editing ? (
                    <textarea
                      value={editNoteBody}
                      onChange={(e) => setEditNoteBody(e.target.value)}
                      rows={Math.max(3, editNoteBody.split("\n").length)}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                    />
                  ) : (
                    <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{n.body}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "billing" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 max-w-2xl">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Billing Configuration</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={billingForm.default_billable}
                disabled={!isAdmin}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, default_billable: e.target.checked })
                }
              />
              New time entries are billable by default
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Hourly Rate (USD)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={billingForm.hourly_rate_dollars}
                disabled={!isAdmin}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, hourly_rate_dollars: Number(e.target.value) })
                }
                className="w-40 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Billing Email
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  (falls back to contact email if blank)
                </span>
              </label>
              <input
                type="email"
                value={billingForm.billing_email}
                disabled={!isAdmin}
                onChange={(e) => setBillingForm({ ...billingForm, billing_email: e.target.value })}
                placeholder={customer.contact_email || "billing@customer.com"}
                className="w-80 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Sales Tax (%)
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  default 6.75% (Ohio); set 0 for tax-exempt customers
                </span>
              </label>
              <input
                type="number"
                min={0}
                max={20}
                step={0.01}
                value={billingForm.sales_tax_pct}
                disabled={!isAdmin}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, sales_tax_pct: Number(e.target.value) })
                }
                className="w-32 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={billingForm.portal_can_view_invoices}
                disabled={!isAdmin}
                onChange={(e) =>
                  setBillingForm({ ...billingForm, portal_can_view_invoices: e.target.checked })
                }
              />
              <span>
                Default invoice access for new portal users
                <span className="block text-xs text-gray-400 font-normal">
                  When inviting a new portal user, this is the initial value of their{" "}
                  <strong>Invoices: on/off</strong> flag. Existing users keep their per-user
                  setting — change it on the Portal Access tab or Users → Customers.
                </span>
              </span>
            </label>
            {isAdmin && (
              <div className="pt-2 flex items-center gap-3">
                <ActionButton onAction={() => saveBilling()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700" pendingLabel="Saving…">
                  Save
                </ActionButton>
                {billingSaved && <span className="text-xs text-green-600">{billingSaved}</span>}
              </div>
            )}
          </div>

          <hr className="my-6 dark:border-gray-700" />

          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Monthly Statement</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            Itemized timesheet + totals + amount due for the selected month, addressed to{" "}
            <strong>
              {customer.billing_email ||
                customer.contact_email ||
                "the customer (set billing email first)"}
            </strong>
            . Sends from the appliance's no-reply address.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-gray-600 dark:text-gray-300">
              Period
              <select
                value={statementMonth}
                onChange={(e) => setStatementMonth(e.target.value)}
                className="ml-2 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
              >
                {monthOptions.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            {isAdmin && (
              <button
                onClick={sendReportPreview}
                disabled={!customer.billing_email && !customer.contact_email}
                className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Send last month's statement
              </button>
            )}
            <button
              onClick={() =>
                downloadFile(`/api/v1/exports/monthly-statement/${id}.pdf`, "statement.pdf", {
                  month: statementMonth,
                })
              }
              className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Download PDF
            </button>
            <button
              onClick={() =>
                downloadFile(`/api/v1/exports/monthly-statement/${id}.csv`, "statement.csv", {
                  month: statementMonth,
                })
              }
              className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              Download CSV
            </button>
          </div>
          {reportInfo && <div className="mt-3 text-xs text-green-700">{reportInfo}</div>}
          {reportError && <div className="mt-3 text-xs text-red-600">{reportError}</div>}
          <p className="text-xs text-gray-400 mt-4">
            The scheduled monthly run fires automatically on the 1st of every month at 8am Eastern.
          </p>
        </div>
      )}

      {tab === "reports" && (
        <div className="space-y-4 max-w-3xl">
          {/* Sub-tabs: Insights (read-only data + customer-facing
            deliverables) vs Policies (admin-editable controls). Keeps
            the tab from being a 10-card scroll. */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 -mt-2 overflow-x-auto">
            {[
              { key: "insights", label: "Insights & deliverables" },
              { key: "policies", label: "Policies & controls" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setReportsSubTab(t.key as "insights" | "policies")}
                className={
                  "px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors " +
                  (reportsSubTab === t.key
                    ? "border-blue-600 text-blue-700 font-semibold"
                    : "border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:text-gray-100")
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className={reportsSubTab === "insights" ? "space-y-4" : "hidden"}>
          {patchCompliance && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Patch Compliance</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Up to date</div>
                  <div className="text-2xl font-bold text-green-700">
                    {patchCompliance.devices_up_to_date}
                    <span className="text-base text-gray-400">
                      /{patchCompliance.total_devices}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Behind</div>
                  <div
                    className={`text-2xl font-bold ${patchCompliance.devices_behind > 0 ? "text-orange-600" : "text-gray-400"}`}
                  >
                    {patchCompliance.devices_behind}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Pending critical
                  </div>
                  <div
                    className={`text-2xl font-bold ${patchCompliance.pending_critical > 0 ? "text-red-600" : "text-gray-400"}`}
                  >
                    {patchCompliance.pending_critical}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Need reboot</div>
                  <div
                    className={`text-2xl font-bold ${patchCompliance.devices_pending_reboot > 0 ? "text-orange-600" : "text-gray-400"}`}
                  >
                    {patchCompliance.devices_pending_reboot}
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                Installed in last 30d:{" "}
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {patchCompliance.installed_last_30d}
                </span>
                {patchCompliance.failed_last_30d > 0 && (
                  <>
                    {" "}
                    · Failures:{" "}
                    <span className="font-medium text-red-600">
                      {patchCompliance.failed_last_30d}
                    </span>
                  </>
                )}
                {patchCompliance.oldest_pending_age_days != null && (
                  <>
                    {" "}
                    · Oldest pending:{" "}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {patchCompliance.oldest_pending_age_days}d
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
              Reports for {customer.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              All downloads below are scoped to this customer only.
            </p>

            <ReportRow
              title="Device inventory"
              description="Hostname, OS, status, agent version, and last-seen for every device assigned to this customer."
              actions={[
                {
                  label: "PDF",
                  onClick: () =>
                    downloadFile("/api/v1/exports/devices.pdf", "devices.pdf", { customer_id: id }),
                },
                {
                  label: "CSV",
                  onClick: () =>
                    downloadFile("/api/v1/exports/devices.csv", "devices.csv", { customer_id: id }),
                },
              ]}
            />

            <ReportRow
              title="Tickets"
              description="Every ticket for this customer with status, priority, assignee, hold reason, and timestamps."
              controls={
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={reportIncludeClosed}
                    onChange={(e) => setReportIncludeClosed(e.target.checked)}
                  />
                  Include closed
                </label>
              }
              actions={[
                {
                  label: "PDF",
                  onClick: () =>
                    downloadFile("/api/v1/exports/tickets.pdf", "tickets.pdf", {
                      customer_id: id,
                      include_closed: reportIncludeClosed,
                    }),
                },
                {
                  label: "CSV",
                  onClick: () =>
                    downloadFile("/api/v1/exports/tickets.csv", "tickets.csv", {
                      customer_id: id,
                      include_closed: reportIncludeClosed,
                    }),
                },
              ]}
            />

            <ReportRow
              title="Time entries"
              description="Itemized tech time logged against this customer in the selected month, billable and non-billable."
              controls={
                <select
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
                >
                  {monthOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              }
              actions={[
                {
                  label: "PDF",
                  onClick: () =>
                    downloadFile("/api/v1/exports/time-entries.pdf", "time-entries.pdf", {
                      customer_id: id,
                      month: reportMonth,
                    }),
                },
                {
                  label: "CSV",
                  onClick: () =>
                    downloadFile("/api/v1/exports/time-entries.csv", "time-entries.csv", {
                      customer_id: id,
                      month: reportMonth,
                    }),
                },
              ]}
            />

            {canViewBilling && (
              <ReportRow
                title="Monthly service statement"
                description="Itemized timesheet + totals + amount due for the selected month. PDF is invoice-ready; CSV is for spreadsheets."
                controls={
                  <select
                    value={reportPriorMonth}
                    onChange={(e) => setReportPriorMonth(e.target.value)}
                    className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                }
                actions={[
                  {
                    label: "PDF",
                    onClick: () =>
                      downloadFile(`/api/v1/exports/monthly-statement/${id}.pdf`, "statement.pdf", {
                        month: reportPriorMonth,
                      }),
                  },
                  {
                    label: "CSV",
                    onClick: () =>
                      downloadFile(`/api/v1/exports/monthly-statement/${id}.csv`, "statement.csv", {
                        month: reportPriorMonth,
                      }),
                  },
                ]}
              />
            )}

            <ReportRow
              title="Self-healing events"
              description="Every remediation action (service restarts, etc.) performed on this customer's devices in the selected window."
              controls={
                <select
                  value={reportDays}
                  onChange={(e) => setReportDays(Number(e.target.value))}
                  className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-2 py-1 text-xs"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                  <option value={365}>Last 365 days</option>
                </select>
              }
              actions={[
                {
                  label: "PDF",
                  onClick: () =>
                    downloadFile(
                      "/api/v1/exports/remediation-events.pdf",
                      "remediation-events.pdf",
                      { customer_id: id, days: reportDays },
                    ),
                },
                {
                  label: "CSV",
                  onClick: () =>
                    downloadFile(
                      "/api/v1/exports/remediation-events.csv",
                      "remediation-events.csv",
                      { customer_id: id, days: reportDays },
                    ),
                },
              ]}
              last
            />
          </div>

          {/* Active alerts */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Active Alerts</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Open alerts for {customer.name}. Resolved automatically when the metric drops back
              below thresholds.
            </p>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-y dark:border-gray-700">
                <tr>
                  <SortableTh label="When" sortKey="created_at" sort={alertSort.sort} onSort={alertSort.toggle} />
                  <SortableTh label="Device" sortKey="device_hostname" sort={alertSort.sort} onSort={alertSort.toggle} />
                  <SortableTh label="Metric" sortKey="metric" sort={alertSort.sort} onSort={alertSort.toggle} />
                  <SortableTh label="Severity" sortKey="severity" sort={alertSort.sort} onSort={alertSort.toggle} />
                  <SortableTh label="Value" sortKey="value" sort={alertSort.sort} onSort={alertSort.toggle} />
                  <th className="px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {activeAlerts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-4 text-center text-gray-400">
                      No active alerts.
                    </td>
                  </tr>
                )}
                {sortRows(activeAlerts, alertSort.sort, (a, key) => {
                  switch (key) {
                    case "device_hostname":
                      return (a.device_hostname || `#${a.device_id}`).toLowerCase();
                    case "metric":
                      return (a.metric || "").toLowerCase();
                    case "severity":
                      return ALERT_SEVERITY_RANK[a.severity] ?? -1;
                    case "value":
                      return a.value ?? 0;
                    default:
                      return a.created_at ? Date.parse(a.created_at) : 0;
                  }
                }).map((a) => {
                  const sevColor =
                    a.severity === "critical"
                      ? "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
                      : a.severity === "warning"
                        ? "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300"
                        : "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300";
                  return (
                    <tr key={a.id}>
                      <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDateTime(a.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        {a.device_hostname || `#${a.device_id}`}
                        {a.target ? ` · ${a.target}:` : ""}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{a.metric}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${sevColor}`}>
                          {a.severity}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {a.value != null ? `${a.value.toFixed(1)}%` : ""}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {a.acknowledged_at ? (
                          <span className="text-xs text-gray-400">Acknowledged</span>
                        ) : (
                          canEdit && (
                            <button
                              onClick={() => acknowledgeAlert(a)}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Acknowledge
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Threshold overrides */}
          {canEdit && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Alert Threshold Overrides
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Override the global defaults for this customer. Remove the override to inherit the
                global value.
              </p>
              {globalThresholds.map((gt) => {
                const d = overrideDraft[gt.metric] || gt;
                const hasOverride = !!customerThresholds.find((x) => x.metric === gt.metric);
                const label =
                  gt.metric === "disk_used_pct"
                    ? "Disk Used %"
                    : gt.metric === "memory_used_pct"
                      ? "Memory Used %"
                      : gt.metric === "cpu_used_pct"
                        ? "CPU Used %"
                        : gt.metric === "battery_health_pct"
                          ? "Battery Health % (lower is worse)"
                          : gt.metric;
                return (
                  <div key={gt.metric} className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</div>
                      {hasOverride ? (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                          Override active
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Inheriting global</span>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <NumberCell
                        label="Info"
                        value={d.info_pct}
                        onChange={(v) =>
                          setOverrideDraft({ ...overrideDraft, [gt.metric]: { ...d, info_pct: v } })
                        }
                      />
                      <NumberCell
                        label="Warning"
                        value={d.warn_pct}
                        onChange={(v) =>
                          setOverrideDraft({ ...overrideDraft, [gt.metric]: { ...d, warn_pct: v } })
                        }
                      />
                      <NumberCell
                        label="Critical"
                        value={d.critical_pct}
                        onChange={(v) =>
                          setOverrideDraft({
                            ...overrideDraft,
                            [gt.metric]: { ...d, critical_pct: v },
                          })
                        }
                      />
                      <NumberCell
                        label="Re-alert (hrs)"
                        value={d.re_alert_hours}
                        onChange={(v) =>
                          setOverrideDraft({
                            ...overrideDraft,
                            [gt.metric]: { ...d, re_alert_hours: v },
                          })
                        }
                      />
                    </div>
                    <div className="flex items-center gap-3 mt-3">
                      <button
                        onClick={() => saveOverride(gt.metric)}
                        className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg"
                      >
                        Save override
                      </button>
                      {hasOverride && (
                        <button
                          onClick={() => clearOverride(gt.metric)}
                          className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg"
                        >
                          Clear override
                        </button>
                      )}
                      {overrideSaved === gt.metric && (
                        <span className="text-xs text-green-600">Saved</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quarterly Business Review */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Quarterly Business Review</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Customer-facing PDF summarizing tickets, SLA, patches, and self-healing.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-1">Quarter</label>
                <select
                  value={qbrQuarter}
                  onChange={(e) => {
                    setQbrQuarter(e.target.value);
                    setQbrSentInfo("");
                  }}
                  className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                >
                  {qbrQuarterOptions.map((q, i) => (
                    <option key={q} value={q}>
                      {q.replace("-", " ")}
                      {i === 0 ? " (current — in progress)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={previewQbr}
                  disabled={!qbrQuarter}
                  className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Preview
                </button>
                <button
                  onClick={downloadQbr}
                  disabled={!qbrQuarter}
                  className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                >
                  Download
                </button>
                {canEdit && (
                  <button
                    onClick={openQbrEmailModal}
                    disabled={!qbrQuarter}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    Email to customer
                  </button>
                )}
              </div>
            </div>
            {qbrSentInfo && (
              <div className="mt-3 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-3 py-2 rounded">
                {qbrSentInfo}
              </div>
            )}
          </div>

          {/* Compliance Evidence Package */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Compliance Evidence Package
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  SOC 2 / HIPAA-style evidence: PDF executive summary + ZIP of raw CSVs
                  (audit log, alerts, access review, controls) for the customer's auditor.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <label className="text-xs uppercase text-gray-500 dark:text-gray-400 block mb-1">Period</label>
                <select
                  value={compliancePeriod}
                  onChange={(e) => {
                    setCompliancePeriod(e.target.value);
                    setComplianceSentInfo("");
                  }}
                  className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
                >
                  {compliancePeriodOptions.map((p, i) => (
                    <option key={p} value={p}>
                      {p.replace("-", " ")}
                      {i === 0 ? " (current — in progress)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={previewCompliance}
                  disabled={!compliancePeriod}
                  className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Preview PDF
                </button>
                {isAdmin && (
                  <button
                    onClick={downloadCompliance}
                    disabled={!compliancePeriod}
                    className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    Download ZIP
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={openComplianceEmailModal}
                    disabled={!compliancePeriod}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    Email to customer
                  </button>
                )}
              </div>
            </div>
            {complianceSentInfo && (
              <div className="mt-3 text-xs text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-3 py-2 rounded">
                {complianceSentInfo}
              </div>
            )}
          </div>

          {complianceEmailOpen && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Email Compliance Package — {compliancePeriod}
                  </h2>
                  <button
                    onClick={() => setComplianceEmailOpen(false)}
                    className="text-gray-400 hover:text-gray-700 dark:text-gray-300"
                  >
                    &times;
                  </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Sent from the appliance's no-reply address; replies reach your helpdesk. The ZIP attachment includes the executive PDF + raw CSVs.
                  </p>
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Recipient</label>
                    <input
                      value={complianceEmailOverride}
                      onChange={(e) => setComplianceEmailOverride(e.target.value)}
                      placeholder={customer.contact_email || "(no contact_email on file)"}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Leave blank to use the customer's contact_email (
                      {customer.contact_email || "not set"}). Send is logged in the audit
                      trail.
                    </p>
                  </div>
                </div>
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
                  <button
                    onClick={() => setComplianceEmailOpen(false)}
                    className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendComplianceEmail}
                    disabled={complianceSending}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    {complianceSending ? "Sending…" : "Send package"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {qbrEmailing && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Email QBR — {qbrQuarter}</h2>
                  <button
                    onClick={() => setQbrEmailing(false)}
                    className="text-gray-400 hover:text-gray-700 dark:text-gray-300"
                  >
                    &times;
                  </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Sent from the appliance's no-reply address, with replies directed to your
                    helpdesk. Recipient:
                  </p>
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Recipient</label>
                    <input
                      value={qbrEmailOverride}
                      onChange={(e) => setQbrEmailOverride(e.target.value)}
                      placeholder={customer.contact_email || "(no contact_email on file)"}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Leave blank to use the customer's contact_email (
                      {customer.contact_email || "not set"}). Send is logged in the audit trail.
                    </p>
                  </div>
                </div>
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
                  <button
                    onClick={() => setQbrEmailing(false)}
                    className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendQbrEmail}
                    disabled={qbrSending}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    {qbrSending ? "Sending…" : "Send QBR"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {slaSummarySendOpen && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Send SLA breach summary
                  </h2>
                  <button
                    onClick={() => setSlaSummarySendOpen(false)}
                    className="text-gray-400 hover:text-gray-700 dark:text-gray-300"
                  >
                    &times;
                  </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Period: <strong>last {slaSummaryDays} days</strong>. Sent from the appliance's no-reply address; replies reach your helpdesk.
                  </p>
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Recipient</label>
                    <input
                      value={slaSummaryRecipient}
                      onChange={(e) => setSlaSummaryRecipient(e.target.value)}
                      placeholder={customer.contact_email || "(no contact_email on file)"}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Leave blank to use the customer's contact_email (
                      {customer.contact_email || "not set"}).
                    </p>
                  </div>
                  <label className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={slaSummaryForce}
                      onChange={(e) => setSlaSummaryForce(e.target.checked)}
                    />
                    Force send even when there are no breaches in the period
                  </label>
                  {slaSummaryResult && (
                    <div
                      className={
                        "text-xs rounded p-2 border " +
                        (slaSummaryResult.startsWith("Sent")
                          ? "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
                          : "bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300")
                      }
                    >
                      {slaSummaryResult}
                    </div>
                  )}
                </div>
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
                  <button
                    onClick={() => setSlaSummarySendOpen(false)}
                    className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
                  >
                    Close
                  </button>
                  <button
                    onClick={sendSlaSummary}
                    disabled={slaSummarySending}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    {slaSummarySending ? "Sending…" : "Send"}
                  </button>
                </div>
              </div>
            </div>
          )}

          </div>

          <div className={reportsSubTab === "policies" ? "space-y-4" : "hidden"}>
          {canEdit && patchPolicyDraft && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Patch Approval Policy</h3>
                {patchPolicyHasOverride ? (
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                    Override active
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">Inheriting global</span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Override the global patch approval policy for this customer. Decline rules win over
                approve rules.
              </p>
              <PolicyChipPicker
                label="Auto-approve severities"
                options={SEVERITY_OPTIONS}
                selected={patchPolicyDraft.auto_approve_severities}
                onChange={(v) =>
                  setPatchPolicyDraft({ ...patchPolicyDraft, auto_approve_severities: v })
                }
                color="bg-green-50 text-green-700 border-green-200"
              />
              <PolicyChipPicker
                label="Auto-approve categories"
                options={CATEGORY_OPTIONS}
                selected={patchPolicyDraft.auto_approve_categories}
                onChange={(v) =>
                  setPatchPolicyDraft({ ...patchPolicyDraft, auto_approve_categories: v })
                }
                color="bg-green-50 text-green-700 border-green-200"
              />
              <PolicyChipPicker
                label="Decline severities"
                options={SEVERITY_OPTIONS}
                selected={patchPolicyDraft.decline_severities}
                onChange={(v) =>
                  setPatchPolicyDraft({ ...patchPolicyDraft, decline_severities: v })
                }
                color="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200"
              />
              <PolicyChipPicker
                label="Decline categories"
                options={CATEGORY_OPTIONS}
                selected={patchPolicyDraft.decline_categories}
                onChange={(v) =>
                  setPatchPolicyDraft({ ...patchPolicyDraft, decline_categories: v })
                }
                color="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200"
              />
              <KbListEditor
                label="Approve KBs (overrides decline rules)"
                kbs={patchPolicyDraft.approve_kbs}
                onChange={(v) => setPatchPolicyDraft({ ...patchPolicyDraft, approve_kbs: v })}
                color="bg-green-50 text-green-700 border-green-200"
              />
              <KbListEditor
                label="Decline KBs"
                kbs={patchPolicyDraft.decline_kbs}
                onChange={(v) => setPatchPolicyDraft({ ...patchPolicyDraft, decline_kbs: v })}
                color="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200"
              />
              <div className="flex items-center gap-3 mt-3">
                <ActionButton onAction={() => savePatchPolicyOverride()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg" pendingLabel="Saving…">
                  Save override
                </ActionButton>
                {patchPolicyHasOverride && (
                  <ActionButton onAction={() => clearPatchPolicyOverride()} variant="raw" className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg" pendingLabel="Clearing…">
                    Clear override
                  </ActionButton>
                )}
                {patchPolicySaved && (
                  <span className="text-xs text-green-600">
                    Saved — re-applied to this customer's devices
                  </span>
                )}
              </div>
            </div>
          )}

          {canEdit && slaDraft && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">SLA Policy</h3>
                {slaHasOverride ? (
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full">
                    Override active
                  </span>
                ) : (
                  <span className="text-xs text-gray-400">Inheriting global</span>
                )}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Override response and resolution targets for this customer. Active-time only —
                on-hold periods pause the clock.
              </p>
              <SLAPolicyEditor value={slaDraft} onChange={setSlaDraft} />
              <div className="flex items-center gap-3 mt-3">
                <ActionButton onAction={() => saveSlaOverride()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg" pendingLabel="Saving…">
                  Save override
                </ActionButton>
                {slaHasOverride && (
                  <ActionButton onAction={() => clearSlaOverride()} variant="raw" className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg" pendingLabel="Clearing…">
                    Clear override
                  </ActionButton>
                )}
                {slaSaved && <span className="text-xs text-green-600">Saved</span>}
              </div>
            </div>
          )}

          {canEdit && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">SLA Breach Summary Email</h3>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Customer-facing digest of tickets whose response or resolution SLA was breached
                during the period. Sent automatically every Monday morning (skipped when there
                are no breaches); use these buttons to preview the email or fire one manually.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-2">
                  Last
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={slaSummaryDays}
                    onChange={(e) => setSlaSummaryDays(Number(e.target.value) || 7)}
                    className="w-16 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                  />
                  days
                </label>
                <ActionButton onAction={() => previewSlaSummary()} variant="raw" className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40" pendingLabel="Generating…">
                  Preview
                </ActionButton>
                {isAdmin && (
                  <button
                    onClick={openSlaSummaryModal}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg"
                  >
                    Send to customer…
                  </button>
                )}
              </div>
            </div>
          )}

          {canEdit && maintWindow && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Maintenance Window</h3>
                <label className="text-xs flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={maintWindow.enabled}
                    onChange={(e) => setMaintWindow({ ...maintWindow, enabled: e.target.checked })}
                  />
                  <span
                    className={maintWindow.enabled ? "text-green-700 font-medium" : "text-gray-500 dark:text-gray-400"}
                  >
                    {maintWindow.enabled ? "Enabled" : "Disabled"}
                  </span>
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                When enabled, the patch dispatcher will install approved updates on this customer's
                online devices during the configured window. No installs happen outside the window.
                Times are interpreted in the selected timezone; the window may cross midnight.
              </p>
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">Days of week</div>
                <div className="flex flex-wrap gap-2">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => {
                    const on = maintWindow.days_of_week.includes(i);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          const next = on
                            ? maintWindow.days_of_week.filter((x) => x !== i)
                            : [...maintWindow.days_of_week, i].sort();
                          setMaintWindow({ ...maintWindow, days_of_week: next });
                        }}
                        className={`text-xs px-3 py-1 rounded-full border ${
                          on
                            ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-200"
                            : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                        }`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Start</div>
                  <input
                    type="time"
                    value={maintWindow.start_time}
                    onChange={(e) => setMaintWindow({ ...maintWindow, start_time: e.target.value })}
                    className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-full"
                  />
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">End</div>
                  <input
                    type="time"
                    value={maintWindow.end_time}
                    onChange={(e) => setMaintWindow({ ...maintWindow, end_time: e.target.value })}
                    className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-full"
                  />
                </div>
                <div>
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Timezone</div>
                  <select
                    value={maintWindow.timezone}
                    onChange={(e) => setMaintWindow({ ...maintWindow, timezone: e.target.value })}
                    className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-full"
                  >
                    {[
                      "America/New_York",
                      "America/Chicago",
                      "America/Denver",
                      "America/Los_Angeles",
                      "America/Phoenix",
                      "America/Anchorage",
                      "Pacific/Honolulu",
                      "Etc/UTC",
                    ].map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <label className="text-xs flex items-center gap-2 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={maintWindow.allow_reboot}
                  onChange={(e) =>
                    setMaintWindow({ ...maintWindow, allow_reboot: e.target.checked })
                  }
                />
                <span>
                  Allow agent to reboot devices during the window if an update requires it
                </span>
              </label>

              {maintWindow.allow_reboot && (
                <div className="border-t dark:border-gray-700 pt-4 mb-4">
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">Reboot countdown</div>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      type="number"
                      min={0}
                      max={3600}
                      step={30}
                      value={maintWindow.reboot_deferral_seconds}
                      onChange={(e) =>
                        setMaintWindow({
                          ...maintWindow,
                          reboot_deferral_seconds: Number(e.target.value),
                        })
                      }
                      className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm w-24"
                    />
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      seconds before forced reboot (
                      {Math.round(maintWindow.reboot_deferral_seconds / 60)} min)
                    </span>
                  </div>
                  <div className="text-xs font-semibold text-gray-600 dark:text-gray-300 mb-2">
                    Message shown to logged-in users
                  </div>
                  <textarea
                    value={maintWindow.reboot_message || ""}
                    rows={2}
                    onChange={(e) =>
                      setMaintWindow({ ...maintWindow, reboot_message: e.target.value })
                    }
                    className="border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm w-full"
                  />
                </div>
              )}
              <div className="flex items-center gap-3">
                <ActionButton onAction={() => saveMaintWindow()} variant="raw" className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg" pendingLabel="Saving…">
                  Save window
                </ActionButton>
                {maintSaved && <span className="text-xs text-green-600">Saved</span>}
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {tab === "runbooks" && (
        <div className="space-y-4 max-w-4xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Runbooks</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Procedures scoped to this customer plus global runbooks shared across all
                  customers. Customer-visible runbooks appear in the customer's portal.
                </p>
              </div>
              {canEdit && (
                <button
                  onClick={openRbCreate}
                  className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg"
                >
                  + New Runbook
                </button>
              )}
            </div>
            <table className="w-full text-sm mt-4">
              <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Title" sortKey="title" sort={rbSort.sort} onSort={rbSort.toggle} />
                  <th className="text-left py-2 pr-3">Scope</th>
                  <SortableTh label="Visibility" sortKey="visibility" sort={rbSort.sort} onSort={rbSort.toggle} />
                  <SortableTh label="Steps" sortKey="steps" sort={rbSort.sort} onSort={rbSort.toggle} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {runbooks.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-400 py-6">
                      No runbooks for this customer yet.
                    </td>
                  </tr>
                )}
                {sortRows(runbooks, rbSort.sort, (rb, key) => {
                  switch (key) {
                    case "visibility":
                      return (rb.visibility || "").toLowerCase();
                    case "steps":
                      return rb.steps.length;
                    default:
                      return (rb.title || "").toLowerCase();
                  }
                }).map((rb) => (
                  <tr key={rb.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{rb.title}</div>
                      {rb.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
                          {rb.description}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {rb.customer_id == null ? (
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                          Global
                        </span>
                      ) : (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                          {customer.name}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          rb.visibility === "customer"
                            ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                            : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                        }`}
                      >
                        {rb.visibility === "customer" ? "Customer-visible" : "Internal"}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">{rb.steps.length}</td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      {canEdit && (
                        <button
                          onClick={() => openRbEdit(rb)}
                          className="text-blue-600 text-xs hover:underline mr-3"
                        >
                          Edit
                        </button>
                      )}
                      {user?.role === "admin" && (
                        <button
                          onClick={() => deleteRunbook(rb)}
                          className="text-red-600 text-xs hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(rbCreating || rbEditing) && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {rbEditing ? "Edit Runbook" : "New Runbook"}
                  </h2>
                  <button onClick={closeRbEditor} className="text-gray-400 hover:text-gray-700 dark:text-gray-300">
                    &times;
                  </button>
                </div>
                <div className="px-6 py-4 space-y-4">
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Title</label>
                    <input
                      value={rbDraft.title}
                      onChange={(e) => setRbDraft({ ...rbDraft, title: e.target.value })}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Description</label>
                    <textarea
                      value={rbDraft.description}
                      onChange={(e) => setRbDraft({ ...rbDraft, description: e.target.value })}
                      rows={2}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Scope</label>
                      <select
                        value={rbDraft.customer_id == null ? "global" : "this"}
                        onChange={(e) =>
                          setRbDraft({
                            ...rbDraft,
                            customer_id: e.target.value === "global" ? null : Number(id),
                          })
                        }
                        className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                      >
                        <option value="this">{customer.name} only</option>
                        <option value="global">Global (all customers)</option>
                      </select>
                      {rbDraft.customer_id == null && rbEditing && (
                        <p className="text-[11px] text-amber-600 mt-1">
                          Editing a global runbook — changes affect every customer.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Visibility</label>
                      <select
                        value={rbDraft.visibility}
                        onChange={(e) =>
                          setRbDraft({
                            ...rbDraft,
                            visibility: e.target.value as "internal" | "customer",
                          })
                        }
                        className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                      >
                        <option value="internal">Internal — techs only</option>
                        <option value="customer">Customer-visible — shown in portal</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Steps</label>
                      <button onClick={addRbStep} className="text-xs text-blue-600 hover:underline">
                        + Add step
                      </button>
                    </div>
                    <div className="space-y-3">
                      {rbDraft.steps.map((s, idx) => (
                        <div key={idx} className="border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-mono text-gray-400 w-6">{idx + 1}.</span>
                            <input
                              placeholder="Step title"
                              value={s.title}
                              onChange={(e) => setRbStep(idx, { title: e.target.value })}
                              className="flex-1 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm"
                            />
                            <button
                              onClick={() => moveRbStep(idx, -1)}
                              disabled={idx === 0}
                              className="text-xs text-gray-500 dark:text-gray-400 disabled:text-gray-300 px-1"
                              title="Move up"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => moveRbStep(idx, 1)}
                              disabled={idx === rbDraft.steps.length - 1}
                              className="text-xs text-gray-500 dark:text-gray-400 disabled:text-gray-300 px-1"
                              title="Move down"
                            >
                              ▼
                            </button>
                            <button
                              onClick={() => removeRbStep(idx)}
                              className="text-xs text-red-600 px-1"
                              title="Remove"
                            >
                              ✕
                            </button>
                          </div>
                          <textarea
                            placeholder="Step body / instructions"
                            value={s.body}
                            onChange={(e) => setRbStep(idx, { body: e.target.value })}
                            rows={3}
                            className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1 text-sm font-mono"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
                  <button
                    onClick={closeRbEditor}
                    className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveRunbook}
                    disabled={rbSaving}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    {rbSaving ? "Saving…" : rbEditing ? "Save changes" : "Create runbook"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "credentials" && (
        <div className="space-y-4 max-w-5xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <div className="flex items-center justify-between mb-1">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Credential Vault</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Encrypted at rest with AES-256-GCM. Every reveal is logged in the audit trail.
                  Customers never see these credentials.
                </p>
              </div>
              {/* Technicians too: the API allows them to create and update
                  (only DELETE is admin-only), and a button hidden from someone
                  the server would have said yes to is just a dead end.
                  NOTE the gate is written `user?.role === "admin"` here rather
                  than the `isAdmin` variable defined at the top of this file -
                  which is why grepping for isAdmin missed it. */}
              {canEdit && (
                <button
                  onClick={openCredCreate}
                  className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg"
                >
                  + New Credential
                </button>
              )}
            </div>
            <table className="w-full text-sm mt-4">
              <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Label" sortKey="label" sort={credSort.sort} onSort={credSort.toggle} />
                  <SortableTh label="Category" sortKey="category" sort={credSort.sort} onSort={credSort.toggle} />
                  <SortableTh label="Username" sortKey="username" sort={credSort.sort} onSort={credSort.toggle} />
                  <SortableTh label="Last accessed" sortKey="last_accessed_at" sort={credSort.sort} onSort={credSort.toggle} />
                  <th />
                </tr>
              </thead>
              <tbody>
                {credentials.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-gray-400 py-6">
                      No credentials stored for this customer yet.
                    </td>
                  </tr>
                )}
                {sortRows(credentials, credSort.sort, (c, key) => {
                  switch (key) {
                    case "category":
                      return (c.category || "").toLowerCase();
                    case "username":
                      return (c.username || "").toLowerCase();
                    case "last_accessed_at":
                      return c.last_accessed_at ? Date.parse(c.last_accessed_at) : 0;
                    default:
                      return (c.label || "").toLowerCase();
                  }
                }).map((c) => (
                  <tr key={c.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{c.label}</div>
                      {c.url && (
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-blue-600 hover:underline"
                        >
                          {c.url}
                        </a>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded">
                        {formatCategoryLabel(c.category)}
                      </span>
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                      {c.username || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">
                      {c.last_accessed_at ? (
                        formatRelative(c.last_accessed_at)
                      ) : (
                        <span className="text-gray-400">never</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => revealCredential(c)}
                        className="text-blue-600 text-xs hover:underline mr-3"
                      >
                        Reveal
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => openCredEdit(c)}
                          className="text-gray-600 dark:text-gray-300 text-xs hover:underline mr-3"
                        >
                          Edit
                        </button>
                      )}
                      {/* Delete alone stays admin-only: the vault is often the
                          only copy of a password and there is no undo. */}
                      {isAdmin && (
                        <button
                          onClick={() => deleteCredential(c)}
                          className="text-red-600 text-xs hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(credCreating || credEditing) && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {credEditing ? "Edit Credential" : "New Credential"}
                  </h2>
                  <button onClick={closeCredEditor} className="text-gray-400 hover:text-gray-700 dark:text-gray-300">
                    &times;
                  </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Label</label>
                    <input
                      value={credDraft.label}
                      onChange={(e) => setCredDraft({ ...credDraft, label: e.target.value })}
                      placeholder="e.g. Domain Admin, Cisco ASA"
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Category</label>
                      <select
                        value={credDraft.category}
                        onChange={(e) => setCredDraft({ ...credDraft, category: e.target.value })}
                        className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                      >
                        {(credCategories.length ? credCategories : ["other"]).map((c) => (
                          <option key={c} value={c}>
                            {formatCategoryLabel(c)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Username</label>
                      <input
                        value={credDraft.username}
                        onChange={(e) => setCredDraft({ ...credDraft, username: e.target.value })}
                        className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1 font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">URL</label>
                    <input
                      value={credDraft.url}
                      onChange={(e) => setCredDraft({ ...credDraft, url: e.target.value })}
                      placeholder="https://..."
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs uppercase text-gray-500 dark:text-gray-400">
                        Secret
                        {credEditing && (
                          <span className="ml-2 normal-case text-gray-400">
                            (leave blank to keep existing)
                          </span>
                        )}
                      </label>
                      <button
                        type="button"
                        onClick={generateCredPassword}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Generate 20-char password
                      </button>
                    </div>
                    <input
                      type="text"
                      value={credDraft.secret}
                      onChange={(e) => setCredDraft({ ...credDraft, secret: e.target.value })}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1 font-mono"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Notes</label>
                    {/* Two rows made this a slot, not a field: credential
                        notes are where the awkward detail goes - which jack,
                        which VLAN, who to ring - and reading it back two
                        lines at a time is worse than not having it. Eight
                        rows, and still resizable for the long ones. */}
                    <textarea
                      value={credDraft.notes}
                      onChange={(e) => setCredDraft({ ...credDraft, notes: e.target.value })}
                      rows={8}
                      className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1 resize-y min-h-[8rem]"
                    />
                  </div>
                </div>
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
                  <button
                    onClick={closeCredEditor}
                    className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCredential}
                    disabled={credSaving}
                    className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    {credSaving ? "Saving…" : credEditing ? "Save changes" : "Create credential"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {revealed && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{revealed.label}</h2>
                  <button onClick={closeReveal} className="text-gray-400 hover:text-gray-700 dark:text-gray-300">
                    &times;
                  </button>
                </div>
                <div className="px-6 py-4 space-y-3">
                  {revealError && (
                    <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm px-3 py-2 rounded">
                      {revealError}
                    </div>
                  )}
                  {revealed.username && (
                    <div>
                      <div className="text-xs uppercase text-gray-500 dark:text-gray-400">Username</div>
                      <div className="font-mono text-sm bg-gray-50 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-3 py-2 mt-1">
                        {revealed.username}
                      </div>
                    </div>
                  )}
                  {!revealError && (
                    <div>
                      <div className="text-xs uppercase text-gray-500 dark:text-gray-400 flex items-center justify-between">
                        <span>Secret</span>
                        <button
                          onClick={() => setRevealVisible(!revealVisible)}
                          className="text-blue-600 normal-case hover:underline"
                        >
                          {revealVisible ? "Hide" : "Show"}
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          readOnly
                          type={revealVisible ? "text" : "password"}
                          value={revealed.secret}
                          className="flex-1 font-mono text-sm bg-gray-50 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-3 py-2"
                        />
                        <ActionButton onAction={() => copyRevealed()} variant="raw" className="bg-blue-600 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap" pendingLabel="Working…">
                          {copyConfirm ? "Copied ✓" : "Copy"}
                        </ActionButton>
                      </div>
                    </div>
                  )}
                  <p className="text-[11px] text-amber-600 italic">
                    This access has been logged in the audit trail.
                  </p>
                </div>
                <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end">
                  <button
                    onClick={closeReveal}
                    className="bg-gray-800 text-white text-sm px-4 py-1.5 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "cloud" && isAdmin && (
        <CloudConnectorsCard customerId={Number(id)} />
      )}

      {tab === "meraki_licenses" && (
        <div className="space-y-4">
          <MerakiLicensesCard customerId={Number(id)} isAdmin={isAdmin} />
          <ManualLicensesCard customerId={Number(id)} isAdmin={isAdmin} />
        </div>
      )}

      {tab === "meshcentral" && (
        <MeshCentralTab customerId={Number(id)} isAdmin={isAdmin} />
      )}

      {tab === "portal" && (
        <div className="space-y-4 max-w-3xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Customer Portal Access</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Invite contacts at {customer.name} to log in at{" "}
              <span className="font-mono text-gray-700 dark:text-gray-300">/portal/login</span> — they can view their
              tickets, submit new ones, see their devices, and view past invoices.
            </p>

            <table className="min-w-full text-sm mb-4">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Email" sortKey="email" sort={portalSort.sort} onSort={portalSort.toggle} />
                  <SortableTh label="Name" sortKey="full_name" sort={portalSort.sort} onSort={portalSort.toggle} />
                  <SortableTh label="Last Login" sortKey="last_login" sort={portalSort.sort} onSort={portalSort.toggle} />
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Status
                  </th>
                  <th className="px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {portalUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-gray-400 text-sm">
                      No portal users yet.
                    </td>
                  </tr>
                )}
                {sortRows(portalUsers, portalSort.sort, (u, key) => {
                  switch (key) {
                    case "full_name":
                      return (u.full_name || "").toLowerCase();
                    case "last_login":
                      return u.last_login ? Date.parse(u.last_login) : 0;
                    default:
                      return (u.email || "").toLowerCase();
                  }
                }).map((u) => (
                  <tr key={u.id}>
                    <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                    <td className="px-3 py-2">{u.full_name || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                      {u.last_login ? formatDateTime(u.last_login) : "Never"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${u.is_active ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"}`}
                        >
                          {u.is_active ? "Active" : "Disabled"}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${u.can_view_invoices !== false ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"}`}
                          title={
                            u.can_view_invoices !== false
                              ? "Sees the Invoices link in the portal"
                              : "Invoices link hidden in the portal"
                          }
                        >
                          {u.can_view_invoices !== false ? "Invoices: on" : "Invoices: off"}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right space-x-3 text-xs whitespace-nowrap">
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => togglePortalUser(u)}
                            className="text-blue-500 hover:underline"
                          >
                            {u.is_active ? "Disable" : "Enable"}
                          </button>
                          <button
                            onClick={() => togglePortalUserInvoices(u)}
                            className="text-blue-500 hover:underline"
                          >
                            {u.can_view_invoices !== false ? "Deny Invoices" : "Allow Invoices"}
                          </button>
                          <button
                            onClick={() => setPortalResetTarget(u)}
                            className="text-blue-500 hover:underline"
                          >
                            Reset PW
                          </button>
                          <button
                            onClick={() => deletePortalUser(u)}
                            className="text-red-500 hover:underline"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {isAdmin && (
              <form
                onSubmit={invitePortalUser}
                className="border-t dark:border-gray-700 pt-4 flex flex-wrap items-end gap-2"
              >
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    placeholder="contact@customer.com"
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Full name (optional)</label>
                  <input
                    value={inviteForm.full_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                    placeholder="Jane Doe"
                    className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
                >
                  Invite
                </button>
              </form>
            )}
            {inviteError && <p className="text-sm text-red-500 mt-2">{inviteError}</p>}
          </div>
        </div>
      )}

      {portalResetTarget && (
        <PasswordResetModal
          title="Reset portal password"
          subjectLabel={portalResetTarget.email}
          description={`Reset the portal password for ${portalResetTarget.email}. The user will be required to change it at their next login either way.`}
          onClose={() => setPortalResetTarget(null)}
          onSubmit={handlePortalResetSubmit}
        />
      )}

      {portalPasswordReveal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-2">Portal password generated</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              Initial password for <strong>{portalPasswordReveal.email}</strong>. Shown once — copy
              and send to the contact securely.
            </p>
            <div className="bg-gray-900 text-green-300 font-mono text-base rounded-lg px-3 py-3 select-all break-all mb-3">
              {portalPasswordReveal.password}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => navigator.clipboard?.writeText(portalPasswordReveal.password)}
                className="border dark:border-gray-600 dark:text-gray-200 text-sm px-4 py-2 rounded-lg"
              >
                Copy
              </button>
              <button
                onClick={() => setPortalPasswordReveal(null)}
                className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  color,
  bg,
}: {
  label: string;
  value: string;
  color: string;
  bg: string;
}) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-gray-100 dark:border-gray-700`}>
      <div className={`text-xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mt-1">{label}</div>
    </div>
  );
}

function NumberCell({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase mb-1">{label}</div>
      <input
        type="number"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-20 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-1.5 text-sm"
      />
    </div>
  );
}

function ReportRow({
  title,
  description,
  controls,
  actions,
  last = false,
}: {
  title: string;
  description: string;
  controls?: React.ReactNode;
  actions: Array<{ label: string; onClick: () => void }>;
  last?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-4 py-4 ${last ? "" : "border-b border-gray-100 dark:border-gray-700"}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {controls}
        {actions.map((a) => (
          <button
            key={a.label}
            onClick={a.onClick}
            className="border dark:border-gray-600 dark:text-gray-200 text-sm px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 whitespace-nowrap"
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function OnboardingPreflightBanner() {
  const [pf, setPf] = useState<{
    agent_exe_present: boolean;
    base_url: string;
    agent_secret_set: boolean;
  } | null>(null);
  useEffect(() => {
    api
      .get("/api/v1/onboarding/preflight")
      .then((r) => setPf(r.data))
      .catch(() => setPf(null));
  }, []);
  if (!pf) return null;
  const problems: string[] = [];
  if (!pf.agent_exe_present) {
    problems.push(
      "AeroGauge-Agent.exe is not staged on the server. Any package you generate will not include the agent binary and install.bat will fail (exit 2). The appliance builds it automatically on first boot (and an administrator can rebuild it); wait a minute or two and refresh."
    );
  }
  if (!pf.agent_secret_set) {
    problems.push("Server SECRET_KEY is missing from .env — agents can't authenticate check-ins.");
  }
  if (pf.base_url && pf.base_url.startsWith("http://localhost")) {
    problems.push(
      `Server BASE_URL is "${pf.base_url}". Agents installed on remote endpoints can't reach localhost. Set BASE_URL to a hostname the endpoints can resolve (e.g. https://rmm.example.com) before mass-deploying.`
    );
  }
  if (problems.length === 0) return null;
  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4 max-w-3xl">
      <div className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
        ⚠ Onboarding preflight failed — packages will not install successfully
      </div>
      <ul className="list-disc list-inside text-sm text-amber-900 dark:text-amber-200 space-y-1">
        {problems.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
    </div>
  );
}

function DevicesTab({
  devices,
  isAdmin,
  onChanged,
}: {
  devices: Device[];
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [staleOnly, setStaleOnly] = useState(false);
  const [staleDays, setStaleDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const devSort = useSort("hostname", "asc");

  const filtered = devices.filter((d) => {
    if (!staleOnly) return true;
    if (!d.last_seen) return true; // never-seen = stale
    const ageDays =
      (Date.now() - utcMillis(d.last_seen)) / (1000 * 60 * 60 * 24);
    return ageDays >= staleDays;
  });
  const sortedFiltered = sortRows(filtered, devSort.sort, (d, key) => {
    switch (key) {
      case "ip_address":
        return ipv4ToNumber(d.ip_address);
      case "os_version":
        return (d.os_version || "").toLowerCase();
      case "status":
        return (d.status || "").toLowerCase();
      case "gotoassist_installed":
        return d.gotoassist_installed ? 1 : 0;
      case "last_seen":
        return d.last_seen ? Date.parse(d.last_seen) : 0;
      default:
        return (d.hostname || "").toLowerCase();
    }
  });

  const allSelected =
    filtered.length > 0 && filtered.every((d) => selected.has(d.id));
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((d) => d.id)));
    }
  };
  const toggleOne = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const removeOne = async (d: Device) => {
    if (
      !window.confirm(
        `Permanently remove device "${d.hostname}" from the RMM?\n\n` +
          "This cascade-deletes inventory, storage, agent commands, alerts, " +
          "and patch history for this device. The action is audit-logged but " +
          "cannot be undone."
      )
    )
      return;
    setBusy(true);
    try {
      await api.delete(`/api/v1/devices/${d.id}`);
      onChanged();
    } catch (e: any) {
      alert("Delete failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Permanently remove ${selected.size} device(s) from the RMM?\n\n` +
          "This cascade-deletes all related inventory, alerts, and history. " +
          "Audit-logged but irreversible."
      )
    )
      return;
    setBusy(true);
    try {
      await api.post(`/api/v1/devices/bulk-action`, {
        device_ids: Array.from(selected),
        action: "delete",
      });
      setSelected(new Set());
      onChanged();
    } catch (e: any) {
      alert("Bulk delete failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Filter / action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={staleOnly}
              onChange={(e) => setStaleOnly(e.target.checked)}
            />
            Stale only (offline ≥
            <input
              type="number"
              min={1}
              max={365}
              value={staleDays}
              onChange={(e) => setStaleDays(Number(e.target.value) || 30)}
              className="w-14 border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-1 py-0.5 mx-1 text-xs"
              disabled={!staleOnly}
            />
            days)
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Showing {filtered.length} of {devices.length}
            {selected.size > 0 && (
              <span className="ml-2 text-blue-700">· {selected.size} selected</span>
            )}
          </span>
        </div>
        {isAdmin && selected.size > 0 && (
          <button
            onClick={removeSelected}
            disabled={busy}
            className="text-xs px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded font-medium disabled:opacity-50"
          >
            Remove {selected.size} selected
          </button>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
            <tr>
              {isAdmin && (
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
              )}
              <SortableTh label="Hostname" sortKey="hostname" sort={devSort.sort} onSort={devSort.toggle} />
              <SortableTh label="IP" sortKey="ip_address" sort={devSort.sort} onSort={devSort.toggle} />
              <SortableTh label="OS" sortKey="os_version" sort={devSort.sort} onSort={devSort.toggle} />
              <SortableTh label="Status" sortKey="status" sort={devSort.sort} onSort={devSort.toggle} />
              <SortableTh label="Last Seen" sortKey="last_seen" sort={devSort.sort} onSort={devSort.toggle} />
              {isAdmin && (
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedFiltered.map((d) => (
              <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                {isAdmin && (
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggleOne(d.id)}
                      aria-label={`Select ${d.hostname}`}
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-medium">
                  <Link to={`/devices/${d.id}`} className="text-blue-600 hover:underline">
                    {d.hostname}
                  </Link>
                </td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{d.ip_address || "—"}</td>
                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-xs truncate">
                  {d.os_version || "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${d.status === "online" ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${d.status === "online" ? "bg-green-500" : "bg-red-500"}`}
                    />
                    {d.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {d.last_seen
                    ? formatRelative(d.last_seen)
                    : "Never"}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => removeOne(d)}
                      disabled={busy}
                      className="text-xs px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded disabled:opacity-50"
                      title="Permanently remove this device from the RMM (audit-logged)"
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={isAdmin ? 8 : 6}
                  className="px-4 py-8 text-center text-gray-400"
                >
                  {staleOnly
                    ? `No devices stale ≥${staleDays} days.`
                    : "No devices yet. Use Onboarding to add devices."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type MeshCentralConfig = {
  customer_id: number;
  meshcentral_url: string | null;
  meshcentral_group: string | null;
  meshcentral_install_cmd: string | null;
  meshcentral_login_user: string | null;
  has_login_key: boolean;
};

function MeshCentralTab({ customerId, isAdmin }: { customerId: number; isAdmin: boolean }) {
  const [cfg, setCfg] = useState<MeshCentralConfig | null>(null);
  const [form, setForm] = useState({
    meshcentral_url: "",
    meshcentral_group: "",
    meshcentral_install_cmd: "",
    meshcentral_login_user: "",
    meshcentral_login_key: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/api/v1/customers/${customerId}/meshcentral`);
      setCfg(r.data);
      setForm({
        meshcentral_url: r.data.meshcentral_url || "",
        meshcentral_group: r.data.meshcentral_group || "",
        meshcentral_install_cmd: r.data.meshcentral_install_cmd || "",
        meshcentral_login_user: r.data.meshcentral_login_user || "",
        meshcentral_login_key: "",
      });
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSavedMsg(null);
    try {
      const body: any = { ...form };
      // Don't send empty login_key (would clear) — only send when user typed
      if (!form.meshcentral_login_key) delete body.meshcentral_login_key;
      const r = await api.put(`/api/v1/customers/${customerId}/meshcentral`, body);
      setCfg(r.data);
      setForm((f) => ({ ...f, meshcentral_login_key: "" }));
      setSavedMsg("Saved.");
      setTimeout(() => setSavedMsg(null), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
          MeshCentral Remote Desktop
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Configure this customer's MeshCentral server so techs get a one-click remote desktop
          button on every device. See the Help guide for MeshCentral setup details.
        </p>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-3 py-2 rounded text-sm mb-3">
            {error}
          </div>
        )}
        {savedMsg && (
          <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 px-3 py-2 rounded text-sm mb-3">
            {savedMsg}
          </div>
        )}

        <div className="space-y-3">
          <Field label="MeshCentral URL">
            <input
              value={form.meshcentral_url}
              onChange={(e) => setForm({ ...form, meshcentral_url: e.target.value })}
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
              placeholder="https://mesh.example.com"
              disabled={!isAdmin}
            />
          </Field>
          <Field label="Device group (optional)">
            <input
              value={form.meshcentral_group}
              onChange={(e) => setForm({ ...form, meshcentral_group: e.target.value })}
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
              placeholder="mesh//abc123..."
              disabled={!isAdmin}
            />
          </Field>
          <Field label="MeshAgent install command (paste from MeshCentral)">
            <textarea
              value={form.meshcentral_install_cmd}
              onChange={(e) =>
                setForm({ ...form, meshcentral_install_cmd: e.target.value })
              }
              className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-xs font-mono h-28"
              placeholder='powershell -Command "& { ... mesh.exe -fullinstall }"'
              disabled={!isAdmin}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Copy from MeshCentral → "Add Devices" dialog for this customer's group. Used by
              the "Install MeshAgent" button on Device detail.
            </p>
          </Field>
          <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Single sign-on (optional)
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Set both to enable a fully-authenticated Remote Desktop URL. Without these the
              button still works — MeshCentral will prompt for login the first time, then
              redirect to the device.
            </p>
            <Field label="MeshCentral user (for SSO)">
              <input
                value={form.meshcentral_login_user}
                onChange={(e) =>
                  setForm({ ...form, meshcentral_login_user: e.target.value })
                }
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
                placeholder="admin"
                disabled={!isAdmin}
              />
            </Field>
            <Field
              label={`Login HMAC key (hex) ${
                cfg?.has_login_key ? "— stored; leave blank to keep" : ""
              }`}
            >
              <input
                type="password"
                value={form.meshcentral_login_key}
                onChange={(e) =>
                  setForm({ ...form, meshcentral_login_key: e.target.value })
                }
                className="w-full border dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded px-2 py-1.5 text-sm font-mono"
                placeholder="64-char hex key from MeshCentral config.json"
                disabled={!isAdmin}
              />
            </Field>
          </div>
        </div>

        {isAdmin && (
          <div className="flex justify-end mt-4">
            <button
              onClick={save}
              disabled={busy}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}
